/* ============================================================================
   COOPER PRODUCT PAGE — THE BUY BOX SCRIPT

   Four jobs, and nothing else:
     1. Turn the chosen options into a variant id, a price and an availability.
     2. Re-point the line field's maxlength when the size changes.
     3. Mirror the typed line into the receipt.
     4. Add the paid embroidery add-on as a SECOND cart line when it is ticked.

   No framework, no build step. Re-binds on shopify:section:load, or the section
   is dead in the owner's own theme editor.
   ========================================================================= */
(() => {
  document.documentElement.setAttribute('data-cooper-js', '');

  const json = (el) => {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      /* A malformed setting must not take the buy button with it. */
      console.warn('[pdp] could not read', el.dataset, e);
      return null;
    }
  };

  /* -------------------------------------------------------------------------
     MONEY, AT MODULE SCOPE.

     These two used to live inside the preview closure, where only the
     placeholder widget could reach them. They were hoisted so the REAL buy
     panel can use them too -- see the running total in wire(), which had no way
     to add the embroidery charge to a formatted price without them.

     NO Intl.NumberFormat anywhere near this file: every other price on the page
     is printed by Liquid's `money` filter, and a second opinion about how to
     write a price is how one panel ends up saying 41,80 while the one above it
     says 41.80. printMoney reuses the shop's OWN formatted string as a template
     and swaps the number inside it, so it cannot disagree.
     ---------------------------------------------------------------------- */
  const parseMoney = (text) => {
    if (!text) return null;
    const match = String(text).match(/(\d[\d\s., ]*)/);
    if (!match) return null;
    const raw = match[1].trim();
    /* Whichever of . or , comes LAST is the decimal mark: "1.234,50" and
       "1,234.50" are the same amount written by different countries. */
    const lastComma = raw.lastIndexOf(',');
    const lastDot = raw.lastIndexOf('.');
    const decimalMark = lastComma > lastDot ? ',' : lastDot > lastComma ? '.' : '';
    let normalised = raw;
    if (decimalMark) {
      const cut = raw.lastIndexOf(decimalMark);
      normalised =
        raw.slice(0, cut).replace(/[^\d]/g, '') + '.' + raw.slice(cut + 1).replace(/[^\d]/g, '');
    } else {
      normalised = raw.replace(/[^\d]/g, '');
    }
    const value = Number.parseFloat(normalised);
    if (!Number.isFinite(value)) return null;
    return {value, decimalMark: decimalMark || ',', template: String(text), token: raw};
  };

  const printMoney = (amount, like) => {
    const shown = amount.toFixed(2).replace('.', like.decimalMark);
    return like.template.replace(like.token, shown);
  };

  /**
   * THE PREVIEW'S THUMBNAILS. Scaffolding, like the preview itself: swaps the
   * large frame's source so the layout can be judged against real cloth instead
   * of grey rectangles. The real gallery is the product's own media and this
   * does not touch it.
   */
  function wireThumbs(box) {
    const main = box.querySelector('[data-pv-main]');
    const thumbs = [...box.querySelectorAll('[data-pv-thumb]')];
    if (!main || !thumbs.length) return;

    const mark = (active) => {
      for (const t of thumbs) {
        t.setAttribute('aria-current', t === active ? 'true' : 'false');
      }
    };

    for (const thumb of thumbs) {
      thumb.addEventListener('click', () => {
        main.src = thumb.dataset.pvThumb;
        mark(thumb);
      });
    }
    mark(thumbs[0]);
  }

  /**
   * THE PREVIEW'S PACK TRACK. Scaffolding again: the real one is wired inside
   * wire(), which needs a product form to exist. This exists so the composition
   * can be clicked through before the shop has stock — switching pack has to
   * show the companion tiles or the whole point of the upsell is invisible.
   */
  function wirePreviewPacks(box) {
    const inputs = [...box.querySelectorAll('[data-pv-packs] input[data-pv-pack]')];
    const groups = [...box.querySelectorAll('[data-pv-tiles]')];
    if (!inputs.length) return;

    const panelName = box.querySelector('[data-pv-panel-name]');
    const panelNote = box.querySelector('[data-pv-panel-note]');
    const submit = box.querySelector('[data-pv-submit]');
    const totalNode = box.querySelector('[data-pv-total]');
    const why = box.querySelector('[data-pv-why]');

    /**
     * MONEY, PARSED AND PUT BACK THE WAY IT CAME.
     *
     * The amounts here are strings an owner typed — "49,90 €" — so adding to
     * them means finding the number inside, doing the arithmetic, and printing
     * it back with the same decimal mark and the same currency token in the same
     * place. Reformatting it as en-GB would turn a Czech shop's euro price into
     * something its own customers do not recognise.
     */

    /**
     * THE RUNNING TOTAL: the pack, plus the embroidery ONCE if anything is
     * written at all. It used to print the pack price alone, so typing a line
     * added a charge the button never mentioned — the buyer would have met it
     * for the first time at the checkout, which is the worst possible place.
     */
    const syncTotal = () => {
      if (!totalNode) return;
      const picked = inputs.find((i) => i.checked);
      const base = parseMoney(picked && picked.dataset.pvPrice);
      if (!base) {
        totalNode.textContent = picked ? picked.dataset.pvPrice || '' : '';
        return;
      }

      const written = [...box.querySelectorAll('.cooper-pdp__line')].some((f) => {
        const holder = f.closest('[data-pv-piece]');
        if (holder && holder.hidden) return false;
        return f.value.trim().length > 0;
      });

      const addon = written ? parseMoney(submit && submit.dataset.addonAmount) : null;
      const total = base.value + (addon ? addon.value : 0);
      totalNode.textContent = printMoney(total, base);
    };

    if (submit) {
      submit.addEventListener('click', () => {
        if (why) why.hidden = false;
      });
    }

    const sync = () => {
      const picked = inputs.find((i) => i.checked);
      const want = picked ? Number.parseInt(picked.dataset.pvPack, 10) : 0;
      for (const g of groups) {
        g.hidden = Number.parseInt(g.dataset.pvTiles, 10) !== want;
      }

      /**
       * ONE WORDS FIELD PER PIECE. In the preview the piece count IS the pack's
       * position — the first option adds nothing, the second adds one garment,
       * the third adds two — so a three-garment pack gets three fields. It used
       * to get two, which left the third piece with nowhere to write and the
       * summary underneath honestly reporting it as blank.
       */
      for (const el of box.querySelectorAll('[data-pv-piece]')) {
        const n = Number.parseInt(el.dataset.pvPiece, 10);
        el.hidden = n > want + 1;
      }

      /* The pack changed, so the total changed. Recomputed below rather than
         set here, because it also depends on whether anything is written. */
      syncTotal();

      if (picked && panelName) panelName.textContent = picked.dataset.pvLabel || '';
      if (picked && panelNote) panelNote.textContent = picked.dataset.pvNote || '';
    };

    /**
     * THE PREVIEW'S OWN SUMMARY. Same rows as the real one — garment, colour,
     * words — assembled from the preview controls so that switching pack,
     * colour or companion changes what it says. Without this the block would be
     * a static picture of one configuration, which is the thing that made the
     * whole preview read as broken in the first place.
     */
    const host = box.querySelector('[data-pv-summary-host]');
    const list = box.querySelector('[data-pv-summary]');

    /**
     * THE COMPANIONS AS THE SCREEN SHOWS THEM: name and chosen colour, read off
     * the tiles of whichever pack group is visible, in layout order.
     *
     * Two things read this — the overview inside step 4 and the labels on its
     * piece rows — and they must never disagree, which is the only reason it is
     * a function rather than two similar loops.
     */
    const visibleTiles = () => {
      const shown = groups.find((g) => !g.hidden);
      if (!shown) return [];
      return [...shown.querySelectorAll('.cooper-pdp__mate')].map((tile) => {
        const picked = tile.querySelector('[data-pv-shop-pick]:checked');
        /* THE LISTING IS THE THING BEING BOUGHT, so its title wins over the
           slot's generic name: a slot called "Neck bandana" holding a listing
           called "Terry bandana" is selling the terry one. The slot name is the
           fallback for a slot with nothing chosen in it. */
        const slotName = tile.querySelector('.cooper-pdp__mate-name');
        const listing = picked ? (picked.dataset.listing || '').trim() : '';
        return {
          name: listing || (slotName ? slotName.textContent.trim() : ''),
          colour: picked ? picked.value.trim() : '',
        };
      });
    };

    /**
     * THE ROW SAYS WHAT IS CHOSEN INSIDE IT.
     *
     * A closed panel has to report its own contents or the buyer has to open two
     * of them to find out what he is buying. Prints "Terry bandana · Lime" under
     * the slot's name, and shuts the panel when a choice is made — a menu that
     * stays open after you have picked from it is a menu you dismiss twice.
     */
    const syncShops = () => {
      for (const shop of box.querySelectorAll('[data-pv-shop]')) {
        const out = shop.querySelector('[data-pv-shop-chosen]');
        if (!out) continue;
        const picked = shop.querySelector('[data-pv-shop-pick]:checked');
        out.textContent = picked
          ? [(picked.dataset.listing || '').trim(), picked.value.trim()]
              .filter(Boolean)
              .join(' · ')
          : '';
      }
    };

    const syncSummary = () => {
      if (!host || !list) return;

      const colourInput = box.querySelector('[data-pv-colours] input:checked');
      const colour = colourInput ? colourInput.dataset.pvColour || '' : '';

      /* THE SIZE, WHICH NOTHING READ UNTIL NOW. The preview's size step shipped
         as `<input type="radio" name="pv-size">` with no value at all: measured
         at /?view=pdp, all five radios reported value "on", and a grep for
         pv-size / pvSize over this file returned 0 matches, so the one choice
         that has to travel with a cut-to-measure cap was the one choice the
         preview summary could not see. The markup now carries value +
         data-pv-size (sections/cooper-pdp-buy.liquid, the preview's size step)
         and [data-pv-size] is in the change binding below, so picking a size
         repaints this list.

         PREVIEW ONLY. The real buy box's size radios already carry their value
         and are read at lines 599 and 629 to resolve the variant; this branch is
         the placeholder picker drawn from the `preview_sizes` setting while
         nothing is published.

         ONE SIZE FOR THE ORDER, ON THE FIRST ROW ONLY. The companions have no
         size step of their own — the preview never draws one for them — so
         putting it on every row would print a measurement for a piece nobody
         measured. */
      const sizeInput = box.querySelector('[data-pv-size]:checked');
      const size = sizeInput ? (sizeInput.dataset.pvSize || sizeInput.value || '').trim() : '';
      const fields = [...box.querySelectorAll('.cooper-pdp__line')];
      /* THE COMPANION'S COLOUR NOW LIVES ON ITS OWN PIECE ROW, beside the words
         it will be stitched with, so this reads the rows instead of the tiles
         above them. The tiles kept the photograph and the name — they sell the
         pack, they no longer configure it.

         VISIBLE ROWS ONLY, in DOM order: the pack decides how many pieces exist,
         and the rows line up with fields[1..n] by position, which is the same
         alignment the cart lines use. */
      /* THE COLOUR IS READ OFF THE TILES AGAIN. It spent a round on the piece
         rows in step 4 and moved back to the bundle step, where the buyer picks
         it beside the photograph — so this reads the visible tile group, in the
         order the tiles are laid out, which is the order the cart lines take. */
      const mates = visibleTiles();

      /* EACH ROW CARRIES ITS OWN THREAD, because the thread is chosen per text
         field now. Looked up by piece number rather than by position, so a
         hidden row cannot shift which colour belongs to which line. */
      const threadOf = (n) => {
        const picked = [...box.querySelectorAll('[data-pv-thread]:checked')].find(
          (r) => (Number.parseInt(r.dataset.threadPiece, 10) || 1) === n,
        );
        return picked ? picked.value.trim() : '';
      };

      const rows = [
        {
          name: host.dataset.productTitle || '',
          colour,
          size,
          words: fields[0] ? fields[0].value.trim() : '',
          thread: threadOf(1),
        },
      ];
      mates.forEach((m, i) => {
        rows.push({
          name: m.name,
          colour: m.colour,
          words: fields[i + 1] ? fields[i + 1].value.trim() : '',
          thread: threadOf(i + 2),
        });
      });

      list.textContent = '';

      /* NO THREAD LINE OF ITS OWN ANY MORE. It was one line above the pieces
         when there was one thread for the order; now every piece names its own,
         inside its own row, next to the words it applies to. */

      for (const row of rows) {
        const li = document.createElement('li');
        li.className = 'cooper-pdp__summary-row';

        const what = document.createElement('span');
        what.className = 'cooper-pdp__summary-what';
        /* Size sits with the garment and its colour rather than on a line of its
           own: it is the same kind of fact, and a fourth line per row would push
           the summary past the step's collapsed height. row.size is empty on the
           companion rows, and .filter(Boolean) drops it there. */
        what.textContent = [row.name, row.colour, row.size].filter(Boolean).join(' · ');
        li.append(what);

        const says = document.createElement('span');
        if (row.words) {
          says.className = 'cooper-pdp__summary-says';
          says.textContent = row.words;
        } else {
          says.className = 'cooper-pdp__summary-blank';
          /* Same label, same source. The preview path is scaffolding, but a
             second English literal is a second thing to find later. */
          says.textContent = box.dataset.blankLabel || 'Nothing written yet';
        }
        li.append(says);

        /* THE THREAD BELONGS TO THE WORDS, so it is printed under them and only
           when there are words to stitch. A blank garment has no thread. */
        if (row.words && row.thread) {
          const thread = document.createElement('span');
          thread.className = 'cooper-pdp__summary-thread';
          thread.textContent = 'in ' + row.thread;
          li.append(thread);
        }

        list.append(li);
      }
    };

    for (const input of inputs) {
      input.addEventListener('change', () => {
        sync();
        syncSummary();
      });
    }
    /* The words change the total as well as the summary: the embroidery charge
       appears the moment anything is written and goes when it is cleared. */
    const onEdit = () => {
      syncShops();
      syncSummary();
      syncTotal();
    };

    for (const el of box.querySelectorAll(
      /* [data-pv-size] added with the size wiring above — without it a size
         choice changed the ring on the label and nothing else. */
      '[data-pv-colours] input, [data-pv-linings] input, .cooper-pdp__picker input, [data-pv-shop-pick], [data-pv-size], .cooper-pdp__line',
    )) {
      el.addEventListener('change', onEdit);
      el.addEventListener('input', onEdit);
    }

    /* A CHOICE SHUTS ITS OWN PANEL, bound here rather than inside syncShops so
       that the initial paint does not close a panel nobody opened. */
    for (const pick of box.querySelectorAll('[data-pv-shop-pick]')) {
      pick.addEventListener('change', () => {
        const shop = pick.closest('[data-pv-shop]');
        if (shop) shop.open = false;
      });
    }

    sync();
    syncShops();
    syncSummary();
    syncTotal();
  }

  /* --------------------------------------------------------------------------
     THE FOURTH STEP'S DOOR.

     The open and closed states belong to <details> and nothing here touches
     them. This adds the two things the element cannot do on its own.

     ONE: opening is an intent to type, so the caret goes where the buyer was
     already going. Without it he taps the row, watches a panel appear, and then
     has to tap again to reach the field he opened it for.

     TWO: a shut door reports what is behind it. Once anything is written the
     teaser is replaced by the buyer's own words and the price chip turns into a
     receipt — so a collapsed step can never hide a charge, which is the whole
     reason it is allowed to collapse.
     ----------------------------------------------------------------------- */
  /* --------------------------------------------------------------------------
     THE COLOUR PICKERS.

     <details> owns open and closed; this does the two things it cannot. It
     paints the chosen swatch onto the closed face — the whole point of the
     control is that you can see the current colour without opening it — and it
     shuts the door after a choice, because a menu that stays open after you have
     picked from it is a menu you have to dismiss twice.

     THE FACE IS CLONED, not read as a colour. An option's swatch is a hex on the
     preview and a variant PHOTOGRAPH on a real listing; copying the inline style
     and the inner markup carries either without this file having to know which.
     ----------------------------------------------------------------------- */
  function wirePickers(root) {
    const scope = root instanceof Element ? root : document;
    for (const picker of scope.querySelectorAll(
      '.cooper-pdp__picker:not([data-picker-bound])',
    )) {
      picker.dataset.pickerBound = '';
      const face = picker.querySelector('[data-picker-face]');
      if (!face) continue;

      const paintFace = () => {
        const chosen = picker.querySelector('input:checked');
        if (!chosen) return;
        const src = chosen.parentElement
          ? chosen.parentElement.querySelector('.cooper-pdp__dot-face')
          : null;
        if (!src) return;
        face.setAttribute('style', src.getAttribute('style') || '');
        face.innerHTML = src.innerHTML;
        /* The blank-variant modifier is a class, so it has to travel too. */
        face.classList.toggle(
          'cooper-pdp__dot-face--blank',
          src.classList.contains('cooper-pdp__dot-face--blank'),
        );
      };

      for (const input of picker.querySelectorAll('input[type="radio"]')) {
        input.addEventListener('change', () => {
          paintFace();
          picker.open = false;
        });
      }

      paintFace();
    }

    /* Clicking anywhere else closes whichever picker is open. Without it the
       only way out of an opened menu is the swatch that opened it, which is not
       where a hand goes next. */
    if (!document.documentElement.dataset.pickerOutside) {
      document.documentElement.dataset.pickerOutside = '';
      document.addEventListener('click', (event) => {
        for (const open of document.querySelectorAll(
          '.cooper-pdp__picker[open]',
        )) {
          if (!open.contains(event.target)) open.open = false;
        }
      });
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        for (const open of document.querySelectorAll(
          '.cooper-pdp__picker[open]',
        )) {
          open.open = false;
        }
      });
    }
  }

  function wireBait(box) {
    for (const bait of box.querySelectorAll('[data-pdp-bait], [data-pv-bait]')) {
      const echo = bait.querySelector(
        '[data-pdp-bait-echo], [data-pv-bait-echo]',
      );
      const fields = [...bait.querySelectorAll('.cooper-pdp__line')];
      if (!fields.length) continue;

      /* A field inside a [hidden] piece wrapper is not on the screen: the pack
         above decides how many pieces exist, and focusing or echoing one the
         buyer cannot see is worse than doing nothing. */
      const live = () => fields.filter((f) => !f.closest('[hidden]'));

      bait.addEventListener('toggle', () => {
        if (!bait.open) return;
        const first = live()[0];
        /* preventScroll: the browser's own scroll-into-view on focus jumps the
           page while the panel is still expanding, which reads as a glitch. */
        if (first) first.focus({preventScroll: true});
      });

      /**
       * THE ROW LABEL CARRIES THE CHOSEN COLOUR.
       *
       * The dropdown that used to sit beside each field printed its own value.
       * Colour circles cannot, and a caption under each circle rebuilds the wide
       * swatch strip they replaced — so the name goes into the label above the
       * field: "NECK BANDANA · RASPBERRY". Nothing here is colour-only, which
       * also keeps it usable for a buyer who cannot tell raspberry from red.
       *
       * The base name is stashed on first run, because this rewrites the same
       * node every time and would otherwise append to its own output.
       */
      const paintLabels = () => {
        /* The thread's own label carries its chosen colour for the same reason
           the piece rows do: its captions came off with the strip. */
        const threadLabel = bait.querySelector(
          '[data-pv-thread-label], [data-pdp-thread-label]',
        );
        if (threadLabel) {
          if (threadLabel.dataset.baseName === undefined) {
            threadLabel.dataset.baseName = threadLabel.textContent.trim();
          }
          const picked = bait.querySelector(
            '[data-pv-thread]:checked, [data-pdp-thread]:checked',
          );
          threadLabel.textContent = picked
            ? threadLabel.dataset.baseName + ' · ' + picked.value.trim()
            : threadLabel.dataset.baseName;
        }

        /* THE COLOUR IS SHOWN HERE AND CHOSEN ELSEWHERE. The picker moved to the
           bundle step's tile; this row still prints the colour so a buyer typing
           a line can see which garment it lands on. Read off the visible tile
           group, matched to the row by position: row 2 is the first companion. */
        const shownTiles = [
          ...(document.querySelector(
            '[data-pv-tiles]:not([hidden]), [data-pack-tiles]:not([hidden])',
          )?.querySelectorAll('.cooper-pdp__mate') || []),
        ];

        const rows = [...bait.querySelectorAll('[data-pv-piece], [data-pdp-piece]')];
        rows.forEach((row, i) => {
          const label =
            row.querySelector('[data-pv-piece-name]') ||
            row.querySelector('.cooper-pdp__piece-name:not([hidden])');
          if (!label) return;

          if (label.dataset.baseName === undefined) {
            label.dataset.baseName = label.textContent.trim();
          }

          const tile = shownTiles[i];
          let name = '';
          if (tile) {
            const picked = tile.querySelector('input[type="radio"]:checked');
            if (picked) {
              /* A real listing's radio value is a variant id, so the readable
                 name comes off the option's own label. */
              const sr = picked.parentElement
                ? picked.parentElement.querySelector('.sr')
                : null;
              name = sr ? sr.textContent.trim() : picked.value.trim();
            }
          }

          label.textContent = name
            ? label.dataset.baseName + ' · ' + name
            : label.dataset.baseName;
        });
      };

      const paint = () => {
        paintLabels();
        const written = live().filter((f) => f.value.trim() !== '');
        bait.classList.toggle(
          'cooper-pdp__bait--filled',
          written.length > 0,
        );

        if (!echo) return;

        if (!written.length) {
          echo.hidden = true;
          echo.textContent = '';
          return;
        }

        /* THE THREAD OF THE PIECE WHOSE WORDS ARE BEING QUOTED, not whichever
           checked radio comes first. Same hidden-copy trap as the overview
           panel: one picker per pack, the inactive ones hidden and still holding
           their default. And the piece has to match, because this line quotes
           written[0] -- on a set with piece one blank it was quoting piece two's
           words beside piece one's colour. */
        const echoPiece = Number.parseInt(written[0].dataset.piece, 10) || 1;
        const thread = [
          ...bait.querySelectorAll(
            '[data-pdp-thread]:checked, [data-pv-thread]:checked',
          ),
        ]
          .filter((r) => !r.closest('[hidden]'))
          .find(
            (r) => (Number.parseInt(r.dataset.threadPiece, 10) || 1) === echoPiece,
          );

        /* Typographic quotes, because the words are being quoted back and a
           straight double quote inside a sentence reads as code. */
        let text = '“' + written[0].value.trim() + '”';
        if (thread) text += ' in ' + thread.value;
        if (written.length > 1) {
          text += ', and ' + (written.length - 1) + ' more';
        }

        echo.hidden = false;
        echo.textContent = text;
      };

      for (const el of bait.querySelectorAll(
        '.cooper-pdp__line, input[type="radio"]',
      )) {
        el.addEventListener('input', paint);
        el.addEventListener('change', paint);
      }

      /* DELEGATED ON THE WHOLE BUY BOX, because the thing that changes a row's
         colour label now lives OUTSIDE this step: the companion picker sits on
         the bundle step's tile. Bound only to inputs inside the door, the label
         would keep printing the colour that was chosen when the page loaded. */
      box.addEventListener('change', paint);

      /* The pack can reveal a second and third field after this has run, and
         those arrive already empty — but a page restored from history can arrive
         with values in them, so the first paint is not skipped. */
      paint();
    }
  }

  /**
   * THE REAL GALLERY. Not the preview's -- the product's own media.
   *
   * WHY IT DID NOT EXIST UNTIL NOW: this page had no product to show for weeks,
   * so only the placeholder preview branch was ever clicked through, and only it
   * was wired. The real branch rendered its thumbnails as plain images. The
   * owner's report on the first live product was exact: one photograph shows and
   * the others cannot be clicked.
   *
   * SETTING src IS NOT ENOUGH, AND THIS IS THE WHOLE TRAP. The frame carries a
   * srcset and a sizes attribute, and the browser picks from the srcset -- so a
   * handler that assigns only src leaves the old picture on screen and looks
   * like a dead button. Both are replaced together, srcset first, and the alt
   * goes with them so what a screen reader announces matches what is on screen.
   *
   * ARROW KEYS MOVE ALONG THE STRIP because it is a row of buttons and a
   * keyboard user should not have to tab through ten of them to reach the last.
   *
   * AND WHY THE PHOTOGRAPH YOU CLICKED USED TO ARRIVE LATE.
   *
   * THE OWNER'S REPORT, 25 August 2026: the photographs lag a little when you
   * switch between them, and the one he pressed does not open straight away.
   *
   * HE WAS DESCRIBING A REAL WAIT, not a slow animation. The old code pointed
   * the lead <img> straight at the full-size CDN file the instant a thumbnail
   * was clicked. That file had never been fetched -- the strip under the frame
   * carries 320px thumbnails and the frame carries a 1400px one -- so the
   * browser did exactly what it is specified to do: it kept painting the OLD
   * photograph until enough of the new one had arrived to replace it.
   *
   * MEASURED before any of this was written, on this store's own navy-cap page:
   * Chrome throttled to Slow 4G, 1440px viewport at DPR 2, cache cleared, then
   * clicking each of the eight frames the page had not yet shown. Time from the
   * click to the first moment the clicked photograph could be painted: 887 ms at
   * best, 1680 ms at worst, 1230 ms in the middle. Every one of those eight
   * large files (56-194 KB) was fetched fresh on the click. For that whole
   * second and a bit the frame showed the PREVIOUS photograph -- and it had
   * already reshaped itself to the new one's aspect ratio, so the buyer watched
   * the old picture shrink inside a box making room for one he could not see
   * yet. The only feedback anywhere was the 2px border moving in the strip.
   *
   * THE FIX IS THREE THINGS, in the order they matter:
   *
   *   1. THE THUMBNAIL HE PRESSED IS ALREADY ON HIS SCREEN, decoded, 7-18 KB of
   *      it. Its own small file goes into the frame as a background the moment
   *      the button is pressed and the stale photograph is lifted out of the
   *      way, so the picture he asked for is there in the same frame as the
   *      click. It is a 320px file in a 660px box and it is soft; it is the
   *      RIGHT picture, which is what he was asking for. Nothing moves when the
   *      sharp file replaces it, because the frame's ratio was set from the
   *      thumbnail's own data attribute in that same first frame.
   *
   *   2. NOTHING IS HANDED TO THE VISIBLE ELEMENT UNTIL IT CAN BE PAINTED. The
   *      large file is fetched by a throwaway Image() and awaited through
   *      decode(); only then is the same srcset assigned to the lead, where it
   *      lands out of cache. The element never points at bytes that have not
   *      arrived, so there is no half-painted frame to see.
   *
   *   3. A DELIBERATE CLICK SHOULD ALMOST ALWAYS HIT CACHE. Hovering, pressing
   *      or tabbing to a thumbnail starts its large file early, and the frame
   *      either side of what is on screen is fetched once the page has gone
   *      idle. Both are deliberately on a leash -- see the note above thrifty().
   */
  function wireGallery(box) {
    const lead = box.querySelector('[data-pdp-lead]');
    const strip = box.querySelector('[data-pdp-thumbs]');
    if (!lead || !strip) return;

    const thumbs = [...strip.querySelectorAll('[data-pdp-thumb]')];
    if (!thumbs.length) return;

    const frame = box.querySelector('.cooper-pdp__frame');

    /* THE CANDIDATE LIST IS BUILT IN ONE PLACE AND SHARED by the preloader and
       the lead, and so is `sizes`. They have to agree to the character: the
       browser chooses which candidate to fetch out of srcset plus sizes, so a
       preloader carrying a different sizes attribute would warm a different file
       and the click would still go to the network having paid for both. */
    const sizes = lead.getAttribute('sizes') || '';
    const candidates = (btn) => {
      const big = btn.dataset.pdpThumb;
      const small = btn.dataset.pdpThumbSmall || big;
      return small + ' 700w, ' + big + ' 1400w';
    };

    /* WHAT HAS ALREADY BEEN FETCHED, keyed by the large file's URL because that
       is what identifies a frame whichever candidate a given screen ends up
       choosing. The value is the Image that fetched it: `complete` with a real
       naturalWidth on that object is the only honest answer to "will pointing
       the frame at this be instant", and it is the lead's answer too, because
       both carry the same srcset and the same sizes. */
    const warm = new Map();
    let turn = 0;
    let at = thumbs.findIndex((t) => t.getAttribute('aria-current') === 'true');
    if (at < 0) at = 0;

    /* The photograph the page loaded with is fetched and decoded already, so the
       lead registers as its own preloader: clicking back to the first frame must
       not go down the waiting path. */
    for (const t of thumbs) {
      if (t.dataset.pdpThumb === lead.getAttribute('src')) warm.set(t.dataset.pdpThumb, lead);
    }

    const heat = (btn) => {
      const big = btn && btn.dataset.pdpThumb;
      if (!big) return null;
      const known = warm.get(big);
      if (known) return known;
      const img = new Image();
      img.decoding = 'async';
      if (sizes) img.sizes = sizes;
      img.srcset = candidates(btn);
      img.src = big;
      warm.set(big, img);
      return img;
    };

    const cached = (img) => !!(img && img.complete && img.naturalWidth > 0);

    /* decode() answers "can this be painted now", and it covers the wait for the
       bytes as well: called on an image that is still loading it settles when the
       load has finished. Safari only grew it in 15.4, hence the event fallback.
       A rejection is not worth reporting -- it means the request failed, we stop
       waiting, and the caller below leaves the holding picture up. */
    const painted = (img) => {
      if (img.decode) {
        return img.decode().then(
          () => img,
          () => img,
        );
      }
      return new Promise((res) => {
        if (img.complete) {
          res(img);
          return;
        }
        img.addEventListener('load', () => res(img), {once: true});
        img.addEventListener('error', () => res(img), {once: true});
      });
    };

    /* THE HOLDING PICTURE. The thumbnail's own bitmap goes in as the frame's
       background and [data-pdp-hold] lifts the stale photograph off it. The
       stylesheet matches background-size: contain to the img's object-fit:
       contain on purpose, so the soft copy and the sharp one that replaces it
       sit at the same size and the upgrade reads as coming into focus rather
       than as a jump.

       currentSrc, not src: it is the candidate the browser actually decoded, and
       it is empty until it has one -- which is the test for whether there is
       anything to borrow at all. */
    const hold = (btn) => {
      if (!frame) return false;
      const own = btn.querySelector('img');
      const tiny = cached(own) ? own.currentSrc || own.src : '';
      if (!tiny) return false;
      frame.style.backgroundImage = 'url("' + tiny + '")';
      frame.setAttribute('data-pdp-hold', '');
      return true;
    };

    /* THE SOFT COPY IS LEFT WHERE IT IS when the sharp file arrives, and that is
       deliberate rather than lazy. It is the understudy the sharp picture fades
       in over -- with it wiped in the same beat the fade would come out of the
       frame's grey instead, which is a flash, not a focus. It is the same
       picture at the same size (contain against contain), so behind an opaque
       photograph it shows nothing; and if the large file failed altogether it is
       what the buyer is left looking at, which beats an empty box. The next swap
       overwrites it. */
    const clear = () => {
      if (!frame) return;
      frame.removeAttribute('data-pdp-hold');
      frame.removeAttribute('data-pdp-blind');
    };

    /* PRELOADING, KEPT ON A LEASH.

       This cap's strip is nine frames and about a megabyte of large files.
       Fetching all of it on load would hand back the 1077 KB that came off the
       phone home page in August, for the sake of a gallery most buyers page two
       frames of. So the speculative half never reaches further than ONE FRAME
       EITHER SIDE of what is on screen, waits for the page to go idle first, and
       does not run at all on a connection that has asked to save data or is
       reporting 2g -- on that connection the holding picture is doing the work
       anyway. Hover, press and focus are not speculation: they warm the one file
       the buyer is reaching for and would have paid for a moment later. */
    const thrifty = () => {
      const c = navigator.connection;
      if (!c) return false;
      return !!c.saveData || c.effectiveType === 'slow-2g' || c.effectiveType === '2g';
    };

    const near = () => {
      heat(thumbs[at + 1]);
      heat(thumbs[at - 1]);
    };

    const idle = (fn) =>
      window.requestIdleCallback ? requestIdleCallback(fn, {timeout: 1500}) : setTimeout(fn, 700);

    const show = (btn) => {
      if (!btn || !btn.dataset.pdpThumb) return;
      const big = btn.dataset.pdpThumb;

      /* EVERY SWAP TAKES A TICKET. The waiting below is asynchronous and image
         requests do not finish in the order they were started: a 56 KB frame
         asked for second lands before a 194 KB one asked for first. Without this
         a buyer clicking three thumbnails quickly could be left looking at the
         second. Only the newest ticket is allowed to touch the lead. */
      const mine = ++turn;
      at = thumbs.indexOf(btn);

      /* THE SHAPE, THE STRIP AND THE NAME MOVE IN THE SAME FRAME AS THE CLICK.
         The ratio comes from the thumbnail's data attribute rather than from the
         loaded image, so the box is the right shape before the picture lands and
         there is only ever ONE resize -- waiting for naturalWidth would mean a
         frame at the old shape and a visible jump. The alt text goes with it
         rather than with the bytes: a screen reader has no use for the holding
         picture and must not wait a second to be told what it is looking at. */
      const ratio = btn.dataset.pdpThumbRatio;
      if (frame && ratio && ratio.indexOf('0') !== 0) {
        frame.style.setProperty('--frame', ratio);
      }
      if (btn.dataset.pdpThumbAlt) lead.alt = btn.dataset.pdpThumbAlt;
      for (const t of thumbs) {
        t.setAttribute('aria-current', t === btn ? 'true' : 'false');
      }

      const land = () => {
        if (mine !== turn) return;
        /* srcset before src: assigning src while a srcset still names another
           candidate is how this looks broken. */
        lead.srcset = candidates(btn);
        lead.src = big;

        /* THE REVEAL WAITS ONE MORE BEAT, and this is not superstition. Assigning
           src does not repaint the element, it starts the "update the image data"
           steps; putting the element back on screen in the same task would show
           the OLD bitmap for one frame, which is a blink back to the picture the
           buyer just left. decode() here is the wait for the VISIBLE element's
           own bitmap, and the bytes are in cache by this point, so it costs a
           frame at most. A rejection means the file failed: reveal anyway and let
           the soft copy underneath stand in. */
        const reveal = () => {
          if (mine !== turn) return;
          clear();
        };
        if (lead.decode) {
          lead.decode().then(reveal, reveal);
        } else {
          reveal();
        }
        if (!thrifty()) idle(near);
      };

      const known = warm.get(big);
      if (cached(known)) {
        land();
        return;
      }

      const held = hold(btn);

      /* NO RING WHERE THERE IS A PICTURE. With the holding picture up, the
         picture IS the feedback and a spinner on top of it would be noise. The
         veil and the ring are for the other case -- a phone's strip scrolls, so
         its far thumbnails are still unloaded and there is no bitmap to borrow --
         and even then only once the wait has outlasted 140 ms, so a quick
         connection never flashes one. */
      let late = 0;
      if (!held && frame) {
        late = setTimeout(() => {
          if (mine === turn) frame.setAttribute('data-pdp-blind', '');
        }, 140);
      }

      painted(heat(btn)).then(() => {
        if (late) clearTimeout(late);
        land();
      });
    };

    thumbs.forEach((btn, i) => {
      btn.addEventListener('click', () => show(btn));

      /* THE HEAD START. pointerenter covers the mouse, where the pointer sits on
         a thumbnail for a moment before the button goes down. pointerdown covers
         the finger, which never hovers at all: the gap between the press and the
         click is the only warning a phone gives, and it is a real head start on
         a fetch measured at over a second. focus covers the keyboard, where
         tabbing onto a thumbnail is as good a declaration of intent as pointing
         at one. */
      btn.addEventListener('pointerenter', () => {
        if (!thrifty()) heat(btn);
      });
      btn.addEventListener('pointerdown', () => heat(btn));
      btn.addEventListener('focus', () => heat(btn));

      btn.addEventListener('keydown', (e) => {
        const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!step) return;
        e.preventDefault();
        const next = thumbs[(i + step + thumbs.length) % thumbs.length];
        next.focus();
        show(next);
      });
    });

    /* THE GALLERY ANSWERS THE COLOUR STEP, and until this existed it did not.
       Choosing a colour moved the variant id, the price and the button, and left
       the photograph on whatever the page loaded with -- so a buyer picking Sky
       Blue went on looking at the brown one. Measured on the code, not guessed:
       the option handler below calls syncVariant, syncPack and syncReceipt, and
       nothing else.

       IT IS AN EVENT RATHER THAN AN EXPORTED FUNCTION because `show` lives in
       this closure with `turn`, `warm` and `at`, and those three are what keep
       fast clicking honest. Handing the function out would let a second caller
       swap the lead without taking a ticket.

       MATCHED BY MEDIA ID, not by URL. The thumbnail carries a 1400px file and
       the variant a different width, so the two URLs never agree; the id is the
       only thing that identifies the same photograph at every size.

       A variant with no image of its own is silence, not a reset: the buyer
       keeps the frame he was looking at rather than being thrown back to the
       first one. */
    box.addEventListener('cooper:pdp:media', (e) => {
      const id = e && e.detail && e.detail.mediaId;
      if (!id) return;
      const want = thumbs.find((t) => String(t.dataset.pdpThumbId) === String(id));
      if (want && want !== thumbs[at]) show(want);
    });

    /* The neighbour of whatever the page opened with, once the page has nothing
       better to do. This is the one that covers a first click made without ever
       pointing at anything -- paging on to the second frame, which is what a
       buyer reading down the gallery does first. */
    if (!thrifty()) idle(near);

    /* THE SIZE-GUIDE JUMP. The owner asked for a way to reach the size chart
       from the size step rather than hunting for it in the strip. The chart is
       one of the product's own frames, and it is identifiable without any new
       setting because its alt text is written by the import: every listing's
       chart begins "Size guide". So the link below finds that frame, shows it,
       and moves focus to the gallery -- no lightbox, no second copy of the
       image, nothing to keep in sync. If a listing ever has no such frame the
       link removes itself rather than sitting there doing nothing. */
    const jump = box.querySelector('[data-pdp-sizeguide]');
    if (jump) {
      /* THE MODAL WINS WHENEVER IT IS ON THE PAGE, and the gallery jump stays
         underneath it as the fallback for a listing whose media has a chart
         frame but whose guide page and chart between them gave the snippet
         nothing to render. Two behaviours, one control, and the buyer never
         gets a button that does nothing: with neither a modal nor a chart
         frame it removes itself, exactly as before. */
      const modal = wireSizeModal();
      if (modal) {
        jump.setAttribute('aria-haspopup', 'dialog');
        jump.addEventListener('pointerenter', modal.warm);
        jump.addEventListener('focus', modal.warm);
        jump.addEventListener('click', (e) => {
          e.preventDefault();
          modal.open(jump);
        });
        return;
      }
      const chart = thumbs.find((t) =>
        (t.dataset.pdpThumbAlt || '').toLowerCase().indexOf('size guide') === 0);
      if (!chart) {
        jump.remove();
      } else {
        /* The chart is warmed as soon as the link is pointed at, for the same
           reason the thumbnails are: by the time the click lands the file is
           usually already here. */
        jump.addEventListener('pointerenter', () => heat(chart));
        jump.addEventListener('focus', () => heat(chart));
        jump.addEventListener('click', (e) => {
          e.preventDefault();
          show(chart);
          chart.focus();
          if (frame && frame.scrollIntoView) {
            frame.scrollIntoView({block: 'center', behavior: 'smooth'});
          }
        });
      }
    }
  }


  /* ------------------------------------------------------------------------
     THE SIZE GUIDE MODAL.

     The owner, 3 September 2026. The control used to page the gallery to the
     chart frame, and measured at 390x844 that meant: the page jumped UP 306px
     away from the size radios; the chart landed 370px wide, where its
     baked-in digits are 7.97px of ink against 11.2px for body text, and it
     needs 520px to read as ordinary type while a 430px phone can only ever
     give it 410; focus went to an unnamed thumbnail button; and the breed
     guide, frame 8 of 8, was never shown at all.

     EVERY MECHANISM BELOW IS INHERITED, NOT INVENTED.
     assets/cooper-companion.js paid for all four on this same page:

       the focus trap, the backdrop, Escape and the return of focus are the
       browser's once showModal() has put the dialog in the top layer;

       THE SCROLL LOCK IS OURS, because a modal dialog blocks clicks behind
       its backdrop and Chrome still scrolls the page under it. The gutter is
       reserved first: hiding the root's overflow takes a scrollbar away and
       everything behind the backdrop jumps sideways, measured at 14px here;

       THE BACKDROP TEST IS GEOMETRIC. ::backdrop is not an element, so a
       press on it targets the dialog itself, and event.target === dialog
       alone would also close the modal when somebody pressed the gap beside
       the heading;

       THE PICTURES HAVE NO src UNTIL IT OPENS. Two 1200px charts fetched on
       every product page a visitor walks past is the mistake that cost this
       page 1077 KB once.
     --------------------------------------------------------------------- */
  /* ONE CONTROLLER PER DIALOG, KEYED BY THE ELEMENT.

     There used to be exactly one size guide on the page -- the primary
     product's -- so a single module-level handle was enough. The set builder
     below the button offers a bandana, a cap and a bucket hat, and each of
     those is a different question: a bandana is measured round the neck, a hat
     across the head. Opening the primary product's guide beside a bandana card
     would answer the wrong one, confidently.

     So the page now carries one dialog per family and this resolves by family,
     falling back to the first when none is named -- which is what every
     existing caller does and why they did not have to change. */
  const sizeModals = new Map();

  function wireSizeModal(family) {
    /* Named family first (a set card asks by role), then the page's OWN dialog,
       and only then whatever is first in the document -- which is the order the
       old code got backwards. Document order is not ownership: the set cards
       print inside the set section, the page's own dialog prints after the
       companion picker at the very end, so `first` was always a set card
       whenever a set was on the page. */
    const dialog =
      (family && document.querySelector('[data-size-modal][data-family="' + family + '"]')) ||
      document.querySelector('[data-size-modal][data-size-primary]') ||
      document.querySelector('[data-size-modal]');
    if (!dialog) return null;
    if (sizeModals.has(dialog)) return sizeModals.get(dialog);

    const root = document.documentElement;
    let opener = null;

    const lock = () => {
      const bar = window.innerWidth - root.clientWidth;
      if (bar > 0) root.style.scrollbarGutter = 'stable';
      root.style.overflow = 'hidden';
    };
    const unlock = () => {
      root.style.overflow = '';
      root.style.scrollbarGutter = '';
    };

    const paint = () => {
      for (const img of dialog.querySelectorAll('[data-size-img]')) {
        const url = img.dataset.src || '';
        if (url && img.getAttribute('src') !== url) img.setAttribute('src', url);
      }
    };

    dialog.addEventListener('close', () => {
      unlock();
      if (opener && document.contains(opener)) opener.focus();
      opener = null;
    });

    const close = () => {
      if (dialog.open && typeof dialog.close === 'function') {
        dialog.close();
        return;
      }
      dialog.removeAttribute('open');
      unlock();
    };

    for (const x of dialog.querySelectorAll('[data-size-close]')) {
      x.addEventListener('click', close);
    }

    /* Escape is the browser's once the dialog is modal: it fires cancel then
       close, and the close handler above does the tidying. This is only for
       the fallback path, where the element is an ordinary box with an open
       attribute and no key does anything on its own. */
    if (typeof dialog.showModal !== 'function') {
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
      });
    }

    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const r = dialog.getBoundingClientRect();
      const inside =
        event.clientX >= r.left &&
        event.clientX <= r.right &&
        event.clientY >= r.top &&
        event.clientY <= r.bottom;
      if (!inside) close();
    });

    const controller = {
      dialog,
      warm: paint,
      open: (button) => {
        if (dialog.open) return;
        opener = button || null;
        paint();
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          /* No top layer in this browser. It opens in flow where it stands,
             which is a worse experience and a working one. */
          dialog.setAttribute('open', '');
          dialog.scrollIntoView({block: 'nearest', behavior: 'smooth'});
        }
        lock();
        /* SCROLLED BACK TO THE TOP HERE AND NOT ON CLOSE. It was on close, and
           it silently did nothing: a dialog that has just closed is display:none,
           an element with no layout box cannot be scrolled, and the assignment is
           dropped while the browser keeps the offset it had. Measured — set 400,
           closed, reopened, still 400. Doing it after showModal, when the box
           exists again, is the moment that works. */
        const body = dialog.querySelector('.cooper-sizes__body');
        if (body) body.scrollTop = 0;
        dialog.focus({preventScroll: true});
      },
    };
    sizeModals.set(dialog, controller);
    return controller;
  }

  /* ==========================================================================
     THE MOBILE STICKY BUY BAR.

     IT DECIDES NOTHING. The control is `type="submit" form="cooper-pdp-form"`,
     so a press is the browser submitting the canonical form: native constraint
     validation runs first (which is what makes an empty personalisation line
     open its <details> and take the focus, exactly as from the main button),
     then the one submit handler in this file builds the one payload from the
     one DOM. Everything below is mirroring — state out of the main button and
     out of the canonical price and size nodes, never recomputed.

     THREE LISTENERS AND TWO OBSERVERS, and that is the whole cost:
       * IntersectionObserver on the main button -- show/hide, no scroll handler
       * MutationObserver on the main button     -- disabled / state / label
       * change on the size group                -- the size chip
       * cooper:pdp:buystate on the box          -- variant availability
       * submit + the form's own reset of the button -- the busy state
     ========================================================================== */
  function wireSticky(box) {
    const bar = document.querySelector('[data-pdp-sticky]');
    const submit = box.querySelector('[data-pdp-submit]');
    if (!bar || !submit) return;

    const cta = bar.querySelector('[data-pdp-sticky-submit]');
    const sizeChip = bar.querySelector('[data-pdp-sticky-size]');
    const priceChip = bar.querySelector('[data-pdp-sticky-price]');
    const priceNode = box.querySelector('[data-pdp-price]');
    const sizeGroup = box.querySelector('[data-pdp-size]');
    if (!cta) return;

    /* THE MAIN BUTTON IS THE TRUTH ABOUT WHETHER THIS ONE CAN BE PRESSED --
       disabled, sold out, incomplete and the wording of all three. Mirrored
       rather than re-derived, so a future change to the main button's rules
       reaches the bar without anyone remembering it exists. */
    const mirror = () => {
      cta.disabled = submit.disabled;
      cta.dataset.state = submit.dataset.state || '';
      /* THE LABEL, NOT THE BUTTON. The button now carries the price beside its
         wording, so reading the whole element would have the bar announcing
         "Add to cart29,90 €" -- there is no whitespace between them, the gap is
         flex. The fallback keeps this working if the span is ever taken out. */
      const labelNode = submit.querySelector('[data-pdp-submit-label]');
      const said = (labelNode || submit).textContent.trim();
      if (said && cta.textContent.trim() !== said) cta.textContent = said;
      /* THE BUSY FLAG CLEARS HERE AND NOWHERE ELSE, because this is the one
         function that knows whether the purchase is still in the air. It used
         to hang off its own observer, which fires only when `disabled` changes
         -- so a resync that found the button already enabled left aria-busy
         behind, and a screen reader went on announcing a control that had
         finished a second ago. Measured with the hat already in the cart. */
      if (!submit.disabled) cta.removeAttribute('aria-busy');
    };

    const wasChip = bar.querySelector('[data-pdp-sticky-was]');
    const wasNode = box.querySelector('[data-pdp-was]');

    /* THE STRUCK PRICE TRAVELS WITH THE PRICE. Owner, 28.09: the bar read
       «S · 750,00 Kc» flat under a buy box saying 750 against 1 100. Mirrored
       from the buy box rather than re-derived, for the reason the whole bar is:
       one answer on the page instead of two that agree until they do not.

       IT FOLLOWS THE BUY BOX'S OWN hidden FLAG. cooper-pdp.js hides that node
       when a set is priced as a set, and a bar still striking the single-garment
       price would claim a reduction that belongs to a different purchase. */
    const mirrorPrice = () => {
      if (priceChip && priceNode) priceChip.textContent = priceNode.textContent.trim();
      if (!wasChip) return;
      const show = !!(wasNode && !wasNode.hidden && wasNode.textContent.trim());
      wasChip.textContent = show ? wasNode.textContent.trim() : '';
      wasChip.hidden = !show;
    };

    const mirrorSize = () => {
      if (!sizeChip) return;
      const picked = sizeGroup && sizeGroup.querySelector('input:checked');
      const value = picked ? picked.value : '';
      sizeChip.textContent = value;
      sizeChip.hidden = !value;
    };

    mirror();
    mirrorPrice();
    mirrorSize();

    new MutationObserver(mirror).observe(submit, {
      attributes: true,
      attributeFilter: ['disabled', 'data-state'],
      childList: true,
      characterData: true,
      subtree: true,
    });

    if (sizeGroup) {
      sizeGroup.addEventListener('change', () => {
        mirrorSize();
        mirrorPrice();
      });
    }
    box.addEventListener('cooper:pdp:buystate', () => {
      mirror();
      mirrorPrice();
      mirrorSize();
    });

    /* AND AGAIN WHEN THE CART ANSWERS. The mirror above is edge-triggered on
       the main button, and an edge that is missed is never recovered from: the
       bar was measured sitting disabled with aria-busy still on it while the
       main button beside it was ready and the item was already in the cart. A
       button that never comes back is worse than one that flickers, so the two
       moments the cart is known to have replied re-read the truth outright
       rather than waiting for a change that has already happened. */
    const resync = () => {
      mirror();
      mirrorPrice();
    };
    document.addEventListener('cooper:cart:open', () => window.setTimeout(resync, 500));
    document.addEventListener('cooper:cart:changed', resync);

    /* WHILE THE REAL BUTTON IS ON SCREEN THERE IS NOTHING TO OFFER. The
       observer watches the button itself rather than a scroll position, so it
       costs nothing per frame and cannot drift from the layout.

       rootMargin pulls the bottom edge up by the bar's own height: without it
       the button reappearing from below would be "visible" underneath the bar
       that is covering it, and the two would fight across the threshold at
       every small scroll -- the flicker loop. */
    /* ----------------------------------------------------------------------
       TWO ANSWERS DECIDE ONE BAR: is the button behind the buyer, and is
       anything else already sitting on the bottom of the screen.

       THE COOKIE BANNER OWNS THAT EDGE UNTIL IT IS ANSWERED. Shopify's consent
       dialog is fixed to the same strip at z-index 2000000 -- measured -- so it
       wins whatever this bar is given and the two would simply overlap. This
       shop has been here before; a consent banner covering a button is already
       in the notes. The bar stands down until the question is answered, which
       is one tap away.

       ASKED FRESH EACH TIME RATHER THAN CACHED. The first build watched the
       banner with a ResizeObserver and cached the answer, and that has a race
       in it: the banner is appended to the document before it is laid out, so
       the first measurement reads a height of nothing and the cached answer is
       already wrong. Reading it at the moment the bar is about to move needs no
       observer and cannot go stale.

       checkVisibility() is the exact question -- is this element actually being
       displayed -- and answering the banner does not remove it from the
       document, it sets display:none, measured. The height fallback is for
       engines without it. */
    const CONSENT = 'shopify-pc__banner';
    const consentUp = () => {
      const el = document.getElementById(CONSENT);
      if (!el) return false;
      if (typeof el.checkVisibility === 'function') return el.checkVisibility();
      return el.getBoundingClientRect().height > 0;
    };

    let past = false;
    const apply = () => show(past && !consentUp());

    const show = (on) => {
      if (on) {
        bar.hidden = false;
        /* THE BROWSER NEEDS A RESOLVED STARTING STYLE or it has nothing to
           animate from, and reading a layout property is what forces one.

           NOT requestAnimationFrame, which was the first attempt: rAF does not
           run while the page is not being painted -- a background tab, and
           every headless check -- so the bar unhid and then simply stayed
           translated off the bottom of the screen, present in the DOM and
           invisible. Measured exactly that way. A reflow read is synchronous
           and does not care whether anyone is looking. */
        void bar.offsetHeight;
        bar.setAttribute('data-shown', '');
      } else {
        bar.removeAttribute('data-shown');
      }
    };

    /* OUT OF VIEW IS NOT ENOUGH -- IT HAS TO BE BEHIND THE BUYER.

       On a phone the buy button sits below the gallery, so at the top of the
       page it is already out of view and the first version put the bar up
       before the reader had scrolled a pixel. That is the duplicate UI the
       brief rules out: two Add to cart buttons on screen at once, one of them
       floating over a page nobody has started reading.

       boundingClientRect.top tells the two cases apart. Above the viewport
       (top < 0) means the button is behind the buyer and the bar is a way
       back to it; below the viewport means it is simply further down the page
       and they will reach it on their own. */
    new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          past = !e.isIntersecting && e.boundingClientRect.top < 0;
        }
        apply();
      },
      {rootMargin: '0px 0px -80px 0px', threshold: 0},
    ).observe(submit);

    /* THE BANNER IS DISMISSED BY A TAP, so one delegated listener is the whole
       mechanism: after any click anywhere, ask again whether the strip is free.
       Cheaper than the two observers it replaces, and it needs no knowledge of
       how the banner is built. */
    document.addEventListener('click', () => apply(), {passive: true});

    /* THE BUSY STATE IS THE MAIN BUTTON'S, and the mirror above already carries
       `disabled` across. aria-busy is added here because the main button does
       not carry one and screen readers on the bar would otherwise hear a
       control that simply stopped responding. Cleared by the same mirror when
       the handler hands the main button back, on success or on failure. */
    const form = submit.form;
    if (form) {
      form.addEventListener('submit', () => {
        /* SHUT SYNCHRONOUSLY, IN THE SAME TASK AS THE PRESS.

           The canonical handler already does this for the main button -- one
           line, `submit.disabled = true`, on the submit event -- and that is
           what makes a double tap there harmless. Mirroring it through the
           MutationObserver below is not good enough: observer callbacks are
           delivered after the current task, and three taps in one burst all
           dispatch before any of them run. Measured exactly that way: three
           presses, three POSTs to /cart/add.js, a bucket hat ×3 in the cart.

           This listener runs during the first press, so the second and third
           find a disabled control and do nothing. It cannot fire on a refused
           submit either: constraint validation runs first, and a form that
           fails it never reaches a submit event. The mirror hands the button
           back when the handler hands the main one back, on success or on
           failure alike. */
        cta.disabled = true;
        cta.setAttribute('aria-busy', 'true');
      });
    }
  }

  function bind(root) {
    const scope = root instanceof Element ? root : document;
    /* Once per page, before the boxes: there is exactly one dialog however
       many controls open it, and the size step asks for it by name. */
    wireSizeModal();
    for (const box of scope.querySelectorAll('.cooper-pdp:not([data-pdp-bound])')) {
      box.dataset.pdpBound = '';
      wireThumbs(box);
      wireGallery(box);
      wirePreviewPacks(box);
      wire(box);
      wireBait(box);
      wirePickers(box);
      wireSticky(box);
    }
  }

  function wire(box) {
    const form = box.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    const variants = json(box.querySelector('[data-pdp-variants]')) || [];
    const letters = json(box.querySelector('[data-pdp-letters]')) || {};
    const idField = box.querySelector('[data-pdp-variant-id]');
    const priceNode = box.querySelector('[data-pdp-price]');
    const wasNode = box.querySelector('[data-pdp-was]');
    const submit = box.querySelector('[data-pdp-submit]');
    const submitTotal = box.querySelector('[data-pdp-submit-total]');
    /* WHAT THE BUTTON SAYS, WITHOUT THE PRICE IT ALSO CARRIES. Declared once so
       the buystate event and anything that listens to it quote the same string
       the sticky bar does. */
    const submitLabelText = () => {
      if (!submit) return '';
      const node = submit.querySelector('[data-pdp-submit-label]');
      return (node || submit).textContent.trim();
    };
    const line = box.querySelector('[data-pdp-line]');
    const receipt = box.querySelector('[data-pdp-receipt]');
    const receiptLine = box.querySelector('[data-pdp-receipt-line]');
    const addon = box.querySelector('[data-pdp-addon]');
    const groups = [...box.querySelectorAll('[data-pdp-option]')];

    /* ----------------------------------------------------------------------
       A PLAIN GARMENT MUST REACH THE CART CARRYING NOTHING.

       The owner's rule: nothing written, or the step never opened, or opened and
       left blank, means it is just a cap. MEASURED on the live page before this
       existed -- /products/navy-cap, size S, step 3 never touched -- where
       new FormData(form) already held
       [["properties[Your line]",""],["properties[Thread]","Red"]] and the cart
       line came back {"Your line":"","Thread":"Red"} at 34.90. A SHUT <details>
       HIDES ITS CONTROLS FROM THE BUYER, NOT FROM THE FORM, which is why "never
       opened" posts the same pair as "opened and left blank": that reading was
       taken with bait.open false.

       IT HAS TO BE FIXED IN THE DOM RATHER THAN IN THE POSTING CODE, because the
       blank case is the one case this file hands back. The submit listener below
       returns early for a single piece with nothing written, cooper-cart.js then
       posts new FormData(form) verbatim, and so whatever these two controls look
       like at that instant IS the cart line.

       THERE ARE ONLY THREE WAYS TO KEEP A CONTROL OUT OF A FORM'S ENTRY LIST --
       no name, disabled, no form owner -- and each control here can use exactly
       one of them. Both rejected alternatives were measured on the live page.

       THE TEXT FIELD CANNOT BE DISABLED, because a disabled field cannot be
       typed into and the input event that would re-enable it can never fire. Its
       name comes off instead, and because it stays inside the form Enter in the
       field still submits -- measured: implicit submission still fired with the
       name attribute gone.

       THE THREAD RADIOS CANNOT LOSE THEIR NAME, because a radio group IS its
       name. Measured: with the name off, clicking Orange while Red was checked
       left BOTH checked, and putting the name back then handed the cart Red --
       the buyer's own choice discarded without a word. Nor can they be disabled:
       the stylesheet in this section already spends
       .cooper-pdp__dot:has(input:disabled) on SOLD OUT, and measured it computes
       to opacity 0.4 and cursor not-allowed on the chosen dot, so the thread the
       buyer picked would read as unavailable. What is left is the form owner. A
       `form` attribute naming an id that belongs to no form leaves the control
       with NO owner, so it is in no form's entry list, while its name, its
       checkedness and its group all survive. Measured: the properties left the
       FormData, exactly one radio stayed checked, clicking Cream while blank
       still repainted the picker face, and Cream was still the colour that
       posted once a word was typed.

       BLANK MEANS BLANK AFTER TRIMMING, and the server draws the line in the
       same place: posting "properties[Your line]= " reached the cart as "" and
       " COOPER " reached it as "COOPER", both measured against /cart.js. So a
       field holding only spaces is a plain cap here and would have been a plain
       cap there anyway.
       ---------------------------------------------------------------------- */
    const linePropServed = line ? line.getAttribute('name') : null;

    /* AN ID THAT MUST NEVER EXIST. A control whose `form` attribute names no
       form in the document has no form owner at all. Measured on the rendered
       product page: seven forms, of which four carry ids -- cooper-pdp-form,
       contact_form, CustomerLoginForm, cooper-footer-subscribe (the two search
       forms and the cart's checkout form have none) -- and
       getElementById('cooper-pdp-nothing-written') returns null. If a fifth
       named form ever took this id these radios would quietly move into it,
       which is why the id says out loud what it is for.

       AND WHY ALL FIVE MOVE TOGETHER. An orphaned radio's group is keyed on its
       NAME AND ITS OWNER, so owner-null radios form their own group, separate
       from the form's. Measured with a probe form on the live page: an owned
       radio and an orphaned radio sharing one name did NOT exclude each other,
       both stayed checked. Moving the five as a set is therefore safe -- one
       stayed checked throughout, clicks and arrow keys both still worked -- but
       it also means two cooper-pdp-buy sections on ONE page, both blank, would
       merge their ten radios into a single group and could leave a written order
       with no thread. There is one instance per template today. */
    const noFormId = 'cooper-pdp-nothing-written';

    /* THE RADIOS THAT WOULD POST, selected by the thing that makes them post
       rather than by their piece number. Piece one's five carry
       name="properties[Thread]"; pieces two and three carry pdp-thread-2 and
       pdp-thread-3, plain group names that reach no cart, and the script builds
       their properties by hand further down. Measured on the live page: fifteen
       [data-pdp-thread] radios, of which this selector returns exactly those
       five. It stays true because nothing here ever touches a radio's name. */
    const postingThreads = () =>
      box.querySelectorAll('[data-pdp-thread][name^="properties["]');

    /* ----------------------------------------------------------------------
       PLAIN OR PERSONALISED, FROM 22.09.2026.

       What decides whether the personalisation properties post is no longer
       "is there anything in the field" but "which radio is checked". The
       machinery below is the same machinery as before -- the name comes off the
       text field, the thread radios lose their form owner -- and only the
       question feeding it has changed.

       WHY THE TYPED VALUE IS STILL CONSULTED ON TOP OF THE MODE. Personalised
       with an empty field is a state the buyer can be in for as long as it takes
       him to type, and during it the form must not be carrying
       properties[Your line]="" waiting for an accelerated button to post it.
       Required stops the ordinary path; this stops the payload. Both, because
       neither alone covers every route out of this page.

       THE VALUE IS KEPT WHEN HE SWITCHES BACK TO PLAIN. Clearing it would throw
       away typing on a toggle a buyer may be doing to compare, and the property
       cannot leak while plain is checked because the name is off the field. The
       cost of keeping it is exactly nothing, and it is the behaviour the brief
       asked for.
       ---------------------------------------------------------------------- */
    const modeRadios = [...box.querySelectorAll('[data-pdp-mode]')];
    const modeSet = box.querySelector('[data-pdp-mode-set]');
    const requiredMsg = (modeSet && modeSet.dataset.pdpRequiredMsg) || '';
    const isPersonalised = () =>
      modeRadios.length === 0 ||
      modeRadios.some((r) => r.checked && r.value === 'personalised');

    /* Every text field the buyer can actually see. A hidden row belongs to a
       pack he did not choose, and `required` on something off-screen is the
       classic "invalid form control is not focusable" dead end: the browser
       refuses to submit and has nowhere to point the message. */
    const visibleLines = () =>
      [...box.querySelectorAll('.cooper-pdp__line')].filter((f) => {
        const holder = f.closest('[data-pdp-piece]');
        if (holder && holder.hidden) return false;
        /* A CLOSED <details> COUNTS AS NOT VISIBLE, and offsetParent does not
           say so -- a control inside one still reports a parent. That gap is
           what produced `An invalid form control with name='' is not
           focusable`: required on a field the browser cannot scroll to, so it
           refused the submit and said nothing to the buyer. Constraint
           validation runs before the submit event, so no handler can rescue it
           afterwards; the only place to be right is here, before it is set. */
        const door = f.closest('details');
        if (door && !door.open) return false;
        return f.offsetParent !== null;
      });

    const syncRequired = () => {
      const on = isPersonalised();
      for (const f of [...box.querySelectorAll('.cooper-pdp__line')]) {
        f.required = false;
        f.setCustomValidity('');
      }
      if (!on) return;
      for (const f of visibleLines()) f.required = true;
    };

    const syncCartProps = () => {
      const written = isPersonalised() && !!line && line.value.trim().length > 0;

      if (line) {
        if (written) {
          if (linePropServed) line.setAttribute('name', linePropServed);
        } else {
          line.removeAttribute('name');
        }
      }

      for (const radio of postingThreads()) {
        if (written) radio.removeAttribute('form');
        else radio.setAttribute('form', noFormId);
      }
    };

    /* The door is the second half of the step above it and exists only for the
       answer that needs it. `hidden` rather than a class: nothing inside a
       hidden subtree is focusable, which is the accessibility half of the same
       requirement, and it is one attribute instead of a stylesheet rule that a
       later specificity fight could win. */
    const bait = box.querySelector('[data-pdp-bait]');

    /* `open` is forced only when the buyer has just asked for the panel. Any
       other sync -- a pack change, a colour change -- leaves it alone, or a
       buyer who collapsed the door after typing would find it reopened under
       him every time he touched something else on the page. */
    const syncDoor = (opening) => {
      if (!bait) return;
      const on = isPersonalised();
      bait.hidden = !on;
      if (on && opening) bait.open = true;
    };

    /* One place the later analytics phase can hook without touching anything
       else here. It deliberately carries the MODE and not the wording: what a
       buyer stitches on his dog's cap is his, and an analytics payload is the
       last place it belongs. Phase 11 replaces the body; the call site stays. */
    const announceMode = (value) => {
      box.dispatchEvent(
        new CustomEvent('cooper:select_personalisation', {
          bubbles: true,
          detail: { mode: value },
        }),
      );
    };

    const persCard = box.querySelector('[data-pdp-pers]');

    const syncMode = (announce) => {
      syncDoor(announce);
      syncRequired();
      syncCartProps();
      /* The card's own open state. The radios remain the truth; this is the
         class that swaps which handle is on screen. */
      if (persCard) persCard.classList.toggle('cooper-pers--on', isPersonalised());
      if (announce) {
        const picked = modeRadios.find((r) => r.checked);
        announceMode(picked ? picked.value : 'plain');
      }
    };

    /* CLOSING THE PANEL ON AN EMPTY FIELD MEANS PLAIN, AND THIS WAS A DEAD END.

       A buyer can collapse the words panel with nothing written in it. The
       field stayed required -- personalised is personalised -- but a control
       inside a closed <details> cannot be focused, so the browser refused the
       submit and logged `An invalid form control with name='' is not
       focusable` while the page said nothing at all. `offsetParent` does not
       catch it: a closed details still reports one. And the submit handler
       cannot fix it either, because constraint validation runs BEFORE the
       submit event, so the handler never ran.

       An empty personalisation is a plain garment -- that is what it posts and
       what it always posted. So closing the door on an empty field answers the
       question the same way, out loud, and the dead end is gone. Words already
       typed keep the choice: those still post, and the buyer meant them. */
    if (bait) {
      bait.addEventListener('toggle', () => {
        /* The door decides what is reachable, so what is required is recomputed
           every time it moves. */
        syncRequired();
        if (bait.open || !isPersonalised()) return;
        if (line && line.value.trim()) return;
        const plain = modeRadios.find((r) => r.value === 'plain');
        if (plain && !plain.checked) {
          plain.checked = true;
          syncMode(false);
        }
      });
    }

    for (const r of modeRadios) {
      r.addEventListener('change', () => {
        syncMode(true);
        /* Moving focus into the field the buyer just asked for saves a tab and
           tells a screen reader the panel arrived. Only on the way in. */
        if (r.checked && r.value === 'personalised' && line) line.focus();
      });
    }

    /* A required field's default message is the browser's ("Please fill out this
       field"), which is neither ours nor translated. Ours is set at the moment
       the browser decides the field is invalid and cleared on the next input, so
       it never sticks around to contradict a field that has since been filled. */
    if (requiredMsg) {
      for (const f of [...box.querySelectorAll('.cooper-pdp__line')]) {
        f.addEventListener('invalid', () => {
          if (f.validity.valueMissing) f.setCustomValidity(requiredMsg);
        });
        f.addEventListener('input', () => f.setCustomValidity(''));
      }
    }

    /* ----------------------------------------------------------------------
       BUILD YOUR COOPER SET.

       Two optional cards below the button. Each is a checkbox with no form
       owner, so neither reaches the cart by itself; the submit handler reads
       what is ticked and builds the extra lines. One route to the cart: the
       garment and whatever was chosen go in ONE POST /cart/add.js with an items
       array, so there is no state where a bandana arrived and the hat did not.

       THE EUROS ARE CALCULATED FROM THE EXACT RATE, NOT THE BADGE. The chips
       read 35% and 69% because that is what the rates round to; the money is
       worked out from 34.68 and 69.35, which is what Shopify actually runs.
       round(1990 x 0.3468) = 690 and round(1990 x 0.6935) = 1380 -- the 6,90
       and the 13,80 the live cart allocates. A rate that is missing or unusable
       hides the euro figures and leaves the badge, because a wrong price is
       worse than no price.

       THE SIZES ARE SYNCHRONISED because the owner verified bandana, cap and
       bucket hat share S to XXL. The select stays visible and editable, an
       override is never overwritten, and a label that does not exist or is out
       of stock falls back to the question -- never to the nearest size.
       ---------------------------------------------------------------------- */
    const setBox = box.querySelector('[data-pdp-set]');
    const setCards = setBox ? [...setBox.querySelectorAll('[data-pdp-set-slot]')] : [];
    const setSummary = setBox && setBox.querySelector('[data-pdp-set-summary]');
    const setTiers = setBox ? [...setBox.querySelectorAll('.cooper-set__tier')] : [];
    const primaryRole = setBox ? setBox.dataset.primaryRole || '' : '';

    const rate = (name) => {
      const raw = setBox ? Number.parseFloat(setBox.dataset[name]) : NaN;
      return Number.isFinite(raw) && raw > 0 && raw < 100 ? raw / 100 : null;
    };
    const duoRate = setBox ? rate('duoPct') : null;
    const fullRate = setBox ? rate('fullPct') : null;

    /* A card with a request in the air is NOT out of date, it is mid-flight.
       Reconciliation must stand off until it lands, or the drawer's repaint --
       which the card's own add triggers -- unticks the box the buyer just
       ticked, a tenth of a second before the add comes back and ticks it
       again. A counter rather than a flag because two cards can be in flight
       at once. */
    let cardBusy = 0;

    const cardParts = (card) => ({
      size: card.querySelector('[data-pdp-set-size]'),
      add: card.querySelector('[data-pdp-set-add]'),
      img: card.querySelector('[data-pdp-set-img]'),
      title: card.querySelector('[data-pdp-set-title]'),
      price: card.querySelector('[data-pdp-set-price]'),
      was: card.querySelector('[data-pdp-set-was]'),
      save: card.querySelector('[data-pdp-set-save]'),
    });

    /* THE REDUCTION ON THE UPSELL CARD, WRITTEN IN ONE PLACE. Owner, 28.09,
       with a screenshot of this card: «на апселах должна тоже быть видна
       скидка» — it printed 725,00 Kc flat while the tile beside it printed
       725 struck through 1 025 with a green chip. The picker swaps this card to
       another garment without re-rendering it, so the figures have to be
       rewritten by hand; hiding rather than emptying keeps the row from
       carrying a stray gap when the chosen piece is not reduced. */
    const paintCardCut = (card, wasText, saveText) => {
      const p = cardParts(card);
      if (p.was) { p.was.textContent = wasText || ''; p.was.hidden = !wasText; }
      if (p.save) { p.save.textContent = saveText || ''; p.save.hidden = !saveText; }
    };

    const cardVariants = (card) => {
      try {
        return JSON.parse(card.dataset.setVariants || '{}');
      } catch (e) {
        return {};
      }
    };

    /* The shop formats its own money; this only has to put a number where the
       digits are, so it borrows a rendered price as a template rather than
       inventing a format that would be wrong in one locale or the other. */
    const moneyLike = (sample, cents) => {
      const digits = (cents / 100).toFixed(2);
      const hasComma = sample.indexOf(',') !== -1 && sample.lastIndexOf(',') > sample.lastIndexOf('.');
      const body = hasComma ? digits.replace('.', ',') : digits;
      return sample.replace(/[\d.,]+/, body);
    };

    const nativeCurrency = setBox ? setBox.dataset.nativeCurrency === '1' : false;
    const moneySample = () => {
      if (setBox && setBox.dataset.primaryMoney) return setBox.dataset.primaryMoney;
      const c = setCards[0];
      return c ? cardParts(c).price.textContent.trim() : '';
    };

    const primarySize = () => {
      const picked = [];
      for (const g of groups) {
        const input = g.querySelector('input:checked');
        if (input && input.value) picked.push(input.value);
      }
      const labels = Object.keys(cardVariants(setCards[0] || {dataset: {}}));
      for (const value of picked) {
        if (labels.indexOf(value) !== -1) return value;
      }
      return picked[0] || '';
    };

    const fillCardSizes = (card, want) => {
      const {size, add} = cardParts(card);
      if (!size) return;
      const map = cardVariants(card);
      const placeholder = size.options[0];
      const had = size.value;
      size.textContent = '';
      size.append(placeholder);
      for (const name of Object.keys(map)) {
        const o = document.createElement('option');
        o.value = name;
        o.textContent = name;
        size.append(o);
      }
      let next = '';
      if (card.dataset.overridden === 'true' && Object.prototype.hasOwnProperty.call(map, had)) {
        next = had;
      } else if (want && Object.prototype.hasOwnProperty.call(map, want)) {
        next = want;
      }
      size.value = next;
      if (add) {
        if (!next && add.checked) add.checked = false;
        add.disabled = !next;
      }
    };

    const setSelected = () =>
      setCards.filter((c) => {
        const {size, add} = cardParts(c);
        return !!(add && add.checked && !add.disabled && size && size.value);
      });

    const cents = (v) => {
      const n = Number.parseInt(v, 10);
      return Number.isFinite(n) ? n : NaN;
    };

    const primaryCents = () => cents(setBox ? setBox.dataset.primaryPrice : '');

    /* THE BANDANA THE SHOPIFY RULE WOULD DISCOUNT. Both live rules take their
       percentage off ONE bandana and nothing else, so this is the line the whole
       saving lands on -- the card when the page is a hat, the product itself
       when the page is a bandana. */
    const bandanaCents = (picked) => {
      if (primaryRole === 'bandana') return primaryCents();
      const card = (picked || setSelected()).find((c) => c.dataset.setRole === 'bandana')
        || setCards.find((c) => c.dataset.setRole === 'bandana');
      return card ? cents(card.dataset.price) : NaN;
    };

    /* ----------------------------------------------------------------------
       THE PANEL READS THE CART, BECAUSE THE CART IS WHAT THE BUYER PAYS.

       It used to count the ticked cards and ASSUME the product this page is
       about was in the basket too. That held while the cards were a selection
       carried by the main button. It stopped holding the moment each card began
       posting its own line: a buyer can now put the bandana and the cap in the
       cart and never add the hat, and the panel announced a full set at 65,90
       while the drawer beside it said 42,90. The owner caught it with both on
       screen at once.

       So nothing here is computed from what is ticked. The tier, the totals and
       the saving all come from /cart.js -- the same numbers the drawer prints,
       which is why they can no longer disagree with it.

       WHICH TIER IS READ OFF SHOPIFY'S OWN ARITHMETIC, not off a count of what
       looks like a hat. `product_type` is translatable and this shop has
       translated it -- Cap becomes Kappe on /de -- so counting types would work
       in English and quietly stop working in German, which is how this theme's
       mistakes always arrive. The discounted line is found instead and its
       allocation tested against the two rates. A rate matches or it does not,
       in every language.
       ---------------------------------------------------------------------- */
    let cartState = null;

    const tierNow = () => {
      const c = cartState;
      if (!c || !c.items || !c.total_discount) return {name: 'none'};

      let base = 0;
      let cut = 0;
      for (const item of c.items) {
        let sum = 0;
        for (const a of item.line_level_discount_allocations || []) sum += a.amount || 0;
        if (sum > cut) {
          cut = sum;
          base = item.original_line_price;
        }
      }
      if (!cut || !base) return {name: 'none'};
      if (fullRate && Math.floor(base * fullRate) === cut) return {name: 'full'};
      if (duoRate && Math.floor(base * duoRate) === cut) return {name: 'duo'};
      /* A saving Shopify applied that neither rate explains is still a saving:
         it is shown without claiming which tier it is. */
      return {name: 'other'};
    };

    /* The chips promise what a tier is worth before anything is in the cart, so
       THIS one is a projection -- and it lives only on the chips, never in the
       panel, which is the line the old code crossed. */
    const savingFor = (tierName) => {
      const r = tierName === 'full' ? fullRate : tierName === 'duo' ? duoRate : null;
      if (!r) return NaN;
      const b = bandanaCents([]);
      return Number.isFinite(b) ? Math.floor(b * r) : NaN;
    };

    /* The chips promise what each tier is worth before anything is chosen. */
    const paintTiers = () => {
      const sample = moneySample();
      for (const el of setTiers) {
        const v = el.querySelector('[data-pdp-tier-value]');
        if (!v) continue;
        const save = savingFor(el.dataset.tier, []);
        if (nativeCurrency && Number.isFinite(save) && sample) {
          v.textContent = (setBox.dataset.copySave || '{{ amount }}')
            .replace('{{ amount }}', moneyLike(sample, save))
            .replace('{{amount}}', moneyLike(sample, save));
        } else if (!v.dataset.fallback) {
          v.dataset.fallback = v.textContent.trim();
        }
      }
    };

    const won = setBox && setBox.querySelector('[data-pdp-set-won]');
    const wonHead = setBox && setBox.querySelector('[data-pdp-set-wonhead]');
    const wonWas = setBox && setBox.querySelector('[data-pdp-set-wonwas]');
    const wonNow = setBox && setBox.querySelector('[data-pdp-set-wonnow]');
    const wonSave = setBox && setBox.querySelector('[data-pdp-set-wonsave]');
    const note = setBox && setBox.querySelector('[data-pdp-set-note]');

    const syncStatus = () => {
      if (!setBox) return;
      const tier = tierNow();
      for (const el of setTiers) {
        const reached =
          (el.dataset.tier === 'duo' && (tier.name === 'duo' || tier.name === 'full')) ||
          (el.dataset.tier === 'full' && tier.name === 'full');
        el.dataset.reached = reached ? 'true' : 'false';
        if (reached) el.setAttribute('aria-current', 'true');
        else el.removeAttribute('aria-current');
      }
      paintTiers();

      /* The nudge is about what is in the cart, not about what is ticked: a
         hat in the basket with no bandana beside it. */
      if (note) {
        const inCart = (id) =>
          !!(cartState && cartState.items && id &&
             cartState.items.some((i) => String(i.product_id) === String(id)));
        let qualifying = 0;
        let bandana = false;
        for (const card of setCards) {
          if (!inCart(card.dataset.productId)) continue;
          if (card.dataset.setRole === 'bandana') bandana = true;
          else qualifying += 1;
        }
        if (inCart(setBox.dataset.primaryId)) {
          if (primaryRole === 'bandana') bandana = true;
          else qualifying += 1;
        }
        const show = qualifying > 0 && !bandana && tier.name === 'none';
        note.hidden = !show;
        note.textContent = show ? setBox.dataset.copyNobandana || '' : '';
      }

      if (!won) return;
      if (tier.name === 'none' || !cartState) {
        won.hidden = true;
        return;
      }

      const sample = moneySample();
      wonHead.textContent =
        tier.name === 'full' ? setBox.dataset.copyFull || '' : setBox.dataset.copyDuo || '';

      if (!nativeCurrency || !sample) {
        wonWas.textContent = '';
        wonNow.textContent = '';
        wonSave.textContent = setBox.dataset.copyApplied || '';
        won.hidden = false;
        return;
      }
      wonWas.textContent = moneyLike(sample, cartState.original_total_price);
      wonNow.textContent = moneyLike(sample, cartState.total_price);
      wonSave.textContent = (setBox.dataset.copySave || '')
        .replace('{{ amount }}', moneyLike(sample, cartState.total_discount))
        .replace('{{amount}}', moneyLike(sample, cartState.total_discount));
      won.hidden = false;
    };

    /* ----------------------------------------------------------------------
       THE TICK IS A CLAIM ABOUT THE CART, SO THE CART GETS TO CORRECT IT.

       The owner hit this: both cards saying `In your cart` beside a drawer that
       said NOTHING IN IT YET, and an add that only worked on the second press.
       The second press was the first one that added anything -- the first press
       was UNticking a box that had no business being ticked, so it ran the
       remove branch and removed nothing.

       Two ways the box gets ahead of the cart. A browser restores form control
       state across a reload and a back-forward, so a tick survives a refresh
       that the cart did not; and the drawer can empty the cart behind this
       page's back, which nothing here was watching. `autocomplete="off"` closes
       the first. This closes the second, and closes the first again for the
       browsers that ignore it.

       Reconciled BY VARIANT, not by product: the line in the cart also says
       which size, so a cap sitting there in L is shown as L rather than as
       whatever the select happened to be left on. `checked` is assigned
       directly and never clicked -- firing change here would post the item
       again, which is the loop this is meant to prevent.
       ---------------------------------------------------------------------- */
    const reconcileCards = () => {
      if (cardBusy) return;
      if (!cartState || !cartState.items) return;
      for (const card of setCards) {
        const {size, add} = cardParts(card);
        if (!add || !size) continue;
        const map = cardVariants(card);
        let found = '';
        for (const label of Object.keys(map)) {
          const id = map[label];
          const line = cartState.items.find(
            (i) => String(i.id) === String(id) && !Object.keys(i.properties || {}).length,
          );
          if (line) {
            found = label;
            break;
          }
        }
        if (found) {
          if (size.value !== found) {
            size.value = found;
            card.dataset.overridden = 'true';
          }
          add.disabled = false;
          add.checked = true;
          card.dataset.inCart = String(map[found]);
        } else {
          add.checked = false;
          delete card.dataset.inCart;
          add.disabled = !size.value;
        }
      }
    };

    const readCart = async () => {
      try {
        cartState = await (await fetch('/cart.js', {headers: {Accept: 'application/json'}})).json();
      } catch (e) {
        cartState = null;
      }
      reconcileCards();
      syncStatus();
    };

    const syncSet = () => {
      const want = primarySize();
      for (const card of setCards) fillCardSizes(card, want);
      syncStatus();
    };

    for (const card of setCards) {
      const {size, add} = cardParts(card);
      if (size) {
        size.addEventListener('change', () => {
          card.dataset.overridden = size.value ? 'true' : 'false';
          if (add) {
            if (!size.value && add.checked) add.checked = false;
            add.disabled = !size.value;
          }
          syncStatus();
        });
      }
      if (add) {
        /* ------------------------------------------------------------------
           THE SET CARD PUTS THE THING IN THE CART. ITSELF. NOW.

           It used to be a selection that rode along with the main button, and
           that produced the defect the owner found: add the hat, then tick the
           bandana, and nothing happens -- press the main button again to make
           it happen and the cart comes back with TWO hats and one bandana.
           Measured on the draft before this change.

           A control labelled `add` that adds nothing, until you press a
           different control that then adds the wrong thing twice, is not worth
           defending. Each card now owns one line: ticking posts it, unticking
           takes it out again, and the main button carries the product the page
           is about and nothing else.

           The discounts do not care how the cart was filled -- both rules read
           its final composition -- so the saving lands exactly as before.
           ------------------------------------------------------------------ */
        add.addEventListener('change', async () => {
          const map = cardVariants(card);
          const want = cardParts(card).size.value;
          const id = map[want];
          syncStatus();
          box.dispatchEvent(
            new CustomEvent('cooper:add_upsell', {
              bubbles: true,
              detail: {selected: add.checked, role: card.dataset.setRole},
            }),
          );
          if (!id) return;

          add.disabled = true;
          cardBusy += 1;
          try {
            if (add.checked) {
              await fetch('/cart/add.js', {
                method: 'POST',
                headers: {'Content-Type': 'application/json', Accept: 'application/json'},
                body: JSON.stringify({items: [{id, quantity: 1}]}),
              });
              card.dataset.inCart = String(id);
              await readCart();
              /* The drawer both opens and refreshes on this, which is the
                 confirmation the old model never gave. */
              document.dispatchEvent(new CustomEvent('cooper:cart:open'));
            } else {
              /* Removing by LINE KEY, not by id: a variant can sit on more than
                 one line once properties differ, and quantity 0 on the wrong
                 one would take out a line the buyer built by hand. */
              const cart = await (await fetch('/cart.js')).json();
              const line = cart.items.find(
                (i) => String(i.id) === String(id) && !Object.keys(i.properties || {}).length,
              );
              if (line) {
                await fetch('/cart/change.js', {
                  method: 'POST',
                  headers: {'Content-Type': 'application/json'},
                  body: JSON.stringify({id: line.key, quantity: 0}),
                });
              }
              delete card.dataset.inCart;
              await readCart();
              document.dispatchEvent(new CustomEvent('cooper:cart:open'));
            }
          } catch (e) {
            /* A failed add must not leave a tick claiming otherwise. */
            add.checked = !add.checked;
            syncStatus();
          }
          cardBusy -= 1;
          add.disabled = false;
          /* The cart is asked once more now that nothing is in flight: the
             repaints that arrived mid-request were skipped on purpose, and
             this is what makes the card agree with the drawer afterwards. */
          reconcileCards();
          syncStatus();
        });
      }

      /* THE GUIDE FOLLOWS THE CARD'S OWN FAMILY. A bandana is measured round
         the neck and a hat across the head; opening the primary product's guide
         here would answer the wrong question confidently. */
      const guide = card.querySelector('[data-pdp-set-guide]');
      if (guide) {
        guide.addEventListener('click', () => {
          const modal = wireSizeModal(card.dataset.setRole);
          if (modal) modal.open(guide);
        });
      }

      const swap = card.querySelector('[data-pdp-set-swap]');
      if (swap) {
        swap.addEventListener('click', () => {
          const dlg = setBox.querySelector('[data-pdp-set-modal="' + card.dataset.setRole + '"]');
          if (!dlg) return;
          if (typeof dlg.showModal === 'function') dlg.showModal();
          else dlg.setAttribute('open', '');
        });
      }
    }

    /* --- the choosers, one per family on this page ------------------------ */
    const sizeMemory = {};

    for (const dlg of setBox ? [...setBox.querySelectorAll('[data-pdp-set-modal]')] : []) {
      const closer = dlg.querySelector('[data-pdp-set-modal-close]');
      if (closer) closer.addEventListener('click', () => dlg.close());
      /* Clicking the backdrop is the gesture everyone tries first, and a native
         dialog reports it as a click on the dialog itself. */
      dlg.addEventListener('click', (event) => {
        if (event.target === dlg) dlg.close();
      });
    }

    for (const choice of setBox ? [...setBox.querySelectorAll('[data-pdp-set-choice]')] : []) {
      choice.addEventListener('change', () => {
        if (!choice.checked) return;
        const role = choice.dataset.pdpSetChoice;
        const card = setCards.find((c) => c.dataset.setRole === role);
        if (!card) return;
        const p = cardParts(card);
        if (p.size && p.size.value) sizeMemory[card.dataset.handle || ''] = p.size.value;

        card.dataset.setVariants = choice.dataset.variants || '{}';
        card.dataset.handle = choice.value;
        card.dataset.productId = choice.dataset.productId || '';
        card.dataset.price = choice.dataset.price || '';
        delete card.dataset.fullPrice;
        if (p.img) {
          p.img.removeAttribute('srcset');
          p.img.src = choice.dataset.img || p.img.src;
        }
        if (p.title) p.title.textContent = choice.dataset.title || '';
        if (p.price) p.price.textContent = choice.dataset.priceMoney || '';
        paintCardCut(card, choice.dataset.wasMoney, choice.dataset.saveMoney);
        /* A different product is a different answer: whatever was ticked for
           the previous one is not a statement about this one. */
        if (p.add) p.add.checked = false;
        const remembered = sizeMemory[choice.value];
        card.dataset.overridden = remembered ? 'true' : 'false';
        fillCardSizes(card, remembered || primarySize());
        syncStatus();
        const dlg = choice.closest('dialog');
        if (dlg && dlg.open) dlg.close();
        const swap = card.querySelector('[data-pdp-set-swap]');
        if (swap) swap.focus();
      });
    }

    for (const g of groups) {
      g.addEventListener('change', () => syncSet());
    }

    if (setBox) {
      syncSet();
      /* The cart may already hold things from an earlier visit, and the drawer
         can empty it behind this page's back, so the panel re-reads on load and
         whenever the drawer is opened. */
      readCart();
      document.addEventListener('cooper:cart:open', () => {
        window.setTimeout(readCart, 400);
      });
      /* A removal made IN THE DRAWER never opened anything, so the open event
         could not cover it and the cards kept claiming products the buyer had
         just taken out. The drawer now announces every repaint and hands over
         the cart it drew, so this costs no second request. */
      document.addEventListener('cooper:cart:changed', (e) => {
        const fresh = e && e.detail && e.detail.cart;
        if (fresh && fresh.items) {
          cartState = fresh;
          reconcileCards();
          syncStatus();
        } else {
          readCart();
        }
      });
      /* THE MAIN BUTTON DOES NOT ANNOUNCE ITSELF. A plain single garment falls
         through to cooper-cart.js, which opens the drawer by calling its own
         open() rather than by dispatching the event above -- so the panel sat
         on `Duo unlocked` while the cart had already become a full set.
         Measured. Two reads because the add is a round trip and how long it
         takes is the buyer's connection, not ours. */
      form.addEventListener('submit', () => {
        window.setTimeout(readCart, 1200);
        window.setTimeout(readCart, 2600);
      });
      /* Back-forward restores the page from memory without re-running any of
         this, ticks and all, so the cart is asked again on the way in. */
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) readCart();
      });
    }

    /* RUN BEFORE ANYTHING IS TYPED. The defect took no interaction at all: the
       page as served was already carrying both properties. */
    syncMode(false);

    /**
     * WHICH OPTION IS WHICH COMES FROM LIQUID, BY NAME.
     *
     * `data-pdp-option` carries the option's 1-based index as Shopify orders it,
     * resolved server-side by matching the option NAME. Reading it off the DOM
     * order instead would silently swap colour and size the day the owner drags
     * one option above the other in the admin.
     */
    const chosen = () => {
      const opts = [];
      for (const g of groups) {
        const i = Number.parseInt(g.dataset.pdpOption, 10) - 1;
        const picked = g.querySelector('input:checked');
        if (i >= 0 && picked) opts[i] = picked.value;
      }
      return opts;
    };

    const findVariant = () => {
      const want = chosen();
      return (
        variants.find((v) =>
          want.every((value, i) => value === undefined || v.options[i] === value),
        ) || null
      );
    };

    const sizeValue = () => {
      const g = box.querySelector('[data-pdp-size]');
      const picked = g && g.querySelector('input:checked');
      return picked ? picked.value : null;
    };

    /**
     * THERE IS NO LETTER LIMIT, AND THIS FUNCTION EXISTS TO MAKE SURE OF IT.
     *
     * It used to read a per-size map and, where a figure was found, set a
     * maxlength and TRUNCATE what the buyer had already typed:
     * line.value.slice(0, limit). The owner's standing instruction is the
     * opposite — "лимита букв нет мы будем подстраивать размер
     * шрифта под заказ" — so a long line is set smaller, not cut. The FAQ
     * that published hard figures ("S 4, M 6, L 8, XL 10, XXL 12") was rewritten
     * the same evening for the same reason.
     *
     * IT WAS DORMANT WHEN IT WAS REMOVED, and that is precisely why it was worth
     * removing. Measured on the live page: the [data-pdp-letters] island serves
     * "{}", so no size ever found a figure and nothing was ever truncated. The
     * landmine was the next person filling that island in good faith and the
     * shop quietly cutting people's dogs' names in half.
     *
     * THE FUNCTION STAYS rather than being deleted, for two reasons: both call
     * sites (the size handler and the wire-time bootstrap) keep working
     * untouched, and it now actively CLEARS any maxlength that reaches the field
     * from anywhere — a server-rendered attribute, the theme editor, a future
     * app — instead of leaving it to be discovered by a customer.
     */
    const applyLimit = () => {
      if (!line) return;
      line.removeAttribute('maxlength');
    };

    /**
     * WHAT YOU GET, ASSEMBLED FROM WHAT IS ACTUALLY CHOSEN.
     *
     * One row per piece: the garment, its colour, and what it will say. This
     * replaced a block that echoed the typed words alone — which left three of
     * the four things a buyer of a personalised pack cannot check anywhere else
     * invisible until the confirmation email.
     *
     * NO ROW IS SKIPPED FOR HAVING NO WORDS YET. A piece with an empty field
     * says so in words; leaving it out would make the pack look smaller than it
     * is at the moment the buyer is counting what he is paying for.
     */
    const summaryList = box.querySelector('[data-pdp-summary]');

    const syncReceipt = () => {
      if (!receipt || !summaryList) return;

      const pack = currentPack();
      const title = receipt.dataset.productTitle || '';
      const colour = (() => {
        const g = groups.find((x) => x.dataset.pdpOption);
        const picked = g && g.querySelector('input:checked');
        return picked ? picked.value : '';
      })();

      const wordsOf = (n) => {
        const field = lines.find(
          (f) => (Number.parseInt(f.dataset.piece, 10) || 1) === n,
        );
        return field ? field.value.trim() : '';
      };

      /* The companions read off their own piece rows, in the order their lines
         will be added to the cart — so the summary reads in the same order as the
         cart it is about to become, and off the very controls the buyer just
         used rather than off a second copy of them. */
      /* OFF THE VISIBLE TILE GROUP, in layout order — the same source the piece
         labels read, so the overview and the labels cannot disagree. The colour
         picker moved back to the tile in the bundle step; the piece rows in
         step 4 only display what was chosen there. */
      const shownGroup = tileGroups.find((g) => !g.hidden);
      const mateRows = shownGroup
        ? [...shownGroup.querySelectorAll('.cooper-pdp__mate')].map((tile) => {
            const nameEl = tile.querySelector('.cooper-pdp__mate-name');
            const picked = tile.querySelector('input[type="radio"]:checked');
            const sr =
              picked && picked.parentElement
                ? picked.parentElement.querySelector('.sr')
                : null;
            return {
              name: nameEl ? nameEl.textContent.trim() : '',
              colour: sr ? sr.textContent.trim() : '',
            };
          })
        : [];

      const rows = [{name: title, colour, words: wordsOf(1)}];
      mateRows.forEach((m, i) => {
        rows.push({name: m.name, colour: m.colour, words: wordsOf(i + 2)});
      });

      /* THE THREAD BELONGS TO THE WORDS, one per piece, so it is printed inside
         each row rather than once at the top. It was a single line above the
         pieces while there was a single thread for the order. */
      const threadOf = (n) => {
        /* HIDDEN COPIES EXCLUDED, and that was the whole bug. This picker is
           emitted once per pack with all but the active one hidden, every copy
           for a given piece carries the same data-thread-piece, and each hidden
           copy still has its default Red checked. Taking the first match in
           document order therefore read a hidden pack's default and printed
           "in Red" over the colour the buyer had just chosen -- on all three
           pieces, on the last panel before the money, while the cart got the
           right thread. chosenMates() twenty lines up already guards this way. */
        const picked = [...box.querySelectorAll('[data-pdp-thread]:checked')]
          .filter((r) => !r.closest('[hidden]'))
          .find((r) => (Number.parseInt(r.dataset.threadPiece, 10) || 1) === n);
        return picked ? picked.value.trim() : '';
      };

      summaryList.textContent = '';
      for (const row of rows) {
        const li = document.createElement('li');
        li.className = 'cooper-pdp__summary-row';

        const what = document.createElement('span');
        what.className = 'cooper-pdp__summary-what';
        what.textContent = [row.name, row.colour].filter(Boolean).join(' · ');
        li.append(what);

        const says = document.createElement('span');
        if (row.words) {
          says.className = 'cooper-pdp__summary-says';
          says.textContent = row.words;
        } else {
          says.className = 'cooper-pdp__summary-blank';
          says.textContent = box.dataset.blankLabel || 'Nothing written yet';
        }
        li.append(says);

        if (row.words) {
          const t = threadOf(rows.indexOf(row) + 1);
          if (t) {
            const thread = document.createElement('span');
            thread.className = 'cooper-pdp__summary-thread';
            thread.textContent = 'in ' + t;
            li.append(thread);
          }
        }

        summaryList.append(li);
      }

      receipt.hidden = rows.length === 0;
    };

    /* THE PRICE AT THE BUTTON IS THE PRICE THAT WILL BE CHARGED.

       The embroidery is a SECOND cart line, added by this same file further
       down, so the garment's own price is not what the buyer pays the moment
       they type a word. Measured before this change: the panel read 34,90 with a
       word typed and the checkout took 41,80. The research the owner
       commissioned puts 40% of abandonment on unexpected extra cost and 12% on
       not being able to see the total, and the DEFAULT path of this shop is a
       written piece -- so the stated price was understating the charge on the
       purchase the shop exists to make.

       ONCE PER ORDER, NOT PER PIECE, which is why this asks whether ANY visible
       line has text rather than counting them. It matches what the add to cart
       actually posts; if the two ever disagree, the cart is right and this is
       wrong, and they are deliberately reading the same fields.

       A HIDDEN PIECE DOES NOT COUNT. Switching pack can leave a filled field
       inside a collapsed piece, and charging for words nobody will see is the
       same class of error in the other direction. */
    const addonCents = () => {
      if (!addon) return 0;
      const raw = Number.parseInt(addon.dataset.addonCents, 10);
      return Number.isFinite(raw) && raw > 0 ? raw : 0;
    };

    const anythingWritten = () =>
      [...box.querySelectorAll('[data-pdp-line]')].some((f) => {
        const holder = f.closest('[data-pdp-piece]');
        if (holder && holder.hidden) return false;
        return f.value.trim().length > 0;
      });

    /* THE OLD PRICE MOVES WITH THE NEW ONE, so the saving between them stays the
       saving the checkout applies.

       This function adds the embroidery charge to the displayed price whenever
       anything is written, for the reason set out above it. Embroidery is free
       today, so the addition is zero and this does nothing at all. The day it is
       priced, a current price that had moved beside a struck price that had not
       would print a smaller reduction than the real one -- an understatement is
       still a wrong figure next to the word "Save". */
    /* THE ANCHOR NOW COMES FROM THE CHOSEN VARIANT, not from the one Liquid
       happened to render first. `data-was` was written once at page load, and on
       a hoodie — 44 € at S rising to 64 € at 5XL — every size but the first
       struck the wrong figure: 2XL showed 1 000 Kc against 1 100, a reduction of
       100 where the real one is 350. The attribute stays as the fallback for a
       product whose variant JSON predates `compareAtFormatted`. */
    const syncWas = (extra, v) => {
      if (!wasNode) return;
      const raw = (v && v.compareAtFormatted) || wasNode.dataset.was;
      if (!raw) { wasNode.hidden = true; return; }
      /* A VARIANT WITH NO ANCHOR HIDES THE NODE rather than keeping the previous
         size's figure: on a product where only some sizes are reduced, leaving
         the last one on screen is a claim about a size nobody selected. */
      if (v && 'compareAt' in v && !v.compareAt) { wasNode.hidden = true; return; }
      wasNode.hidden = false;
      const w = parseMoney(raw);
      wasNode.textContent = w && extra ? printMoney(w.value + extra / 100, w) : raw;
    };

    /* THE BUTTON'S FIGURE IS MIRRORED, NEVER RECOMPUTED. syncPrice below has
       three exits -- a set price, a plain variant price, and a variant price
       with the embroidery added -- and a second implementation would have to
       get all three right forever. Reading the node it just painted gets them
       right by construction, which is the same reason the sticky bar mirrors
       the button instead of working the variant out again. */
    const syncSubmitTotal = () => {
      if (!submitTotal || !priceNode) return;
      const said = priceNode.textContent.trim();
      if (submitTotal.textContent.trim() !== said) submitTotal.textContent = said;
    };

    const syncPriceFigures = (v) => {
      if (!priceNode || !v || !v.priceFormatted) return;

      /* A SET IS PRICED AS A SET, and the single-garment «was» goes away with
         it. The struck figure and its «Save» are the one-piece comparison;
         printed beside a set price they claim a discount that belongs to a
         different purchase. Hidden rather than rewritten: this file does not
         know what the set was worth before, and inventing that number is how
         a price row becomes a complaint. */
      const pack = typeof currentPack === 'function' ? currentPack() : null;
      if (pack && pack.pieces > 1 && pack.priceText) {
        priceNode.textContent = pack.priceText;
        if (wasNode) wasNode.hidden = true;
        return;
      }
      if (wasNode) wasNode.hidden = false;

      const base = parseMoney(v.priceFormatted);
      const extra = anythingWritten() ? addonCents() : 0;
      syncWas(extra, v);
      if (!base || !extra) {
        priceNode.textContent = v.priceFormatted;
        syncCut(v);
        return;
      }
      priceNode.textContent = printMoney(base.value + extra / 100, base);
      syncCut(v);
    };

    /* AND THE GREEN CHIP, WHICH IS THE SAME CLAIM IN WORDS. It was rendered once
       by Liquid beside a figure the script repaints, so on a hoodie it went on
       saying «Save 100 Kc» through every size. The template comes from the chip
       itself — the wording is a setting and the money format a market's, neither
       of which this file gets to invent. */
    const cutNode = box.querySelector('.cooper-pdp__cut');
    const syncCut = (v) => {
      if (!cutNode || !v) return;
      /* THE TEMPLATE COMES FROM LIQUID WITH ITS TOKEN INTACT, in data-tpl. The
         first attempt swapped the number out of the rendered sentence with a
         regular expression, which has to guess at a money format it does not
         own — «1.100,00 Kc» and «1 100,00 Kc» and «€1,100.00» are all the shop's
         own format in some market. A token is exact and needs no guessing. */
      const tpl = cutNode.dataset.tpl;
      const anchor = v.compareAt;
      if (!tpl || !anchor || anchor <= v.price) { cutNode.hidden = true; return; }
      const sample = parseMoney(v.priceFormatted);
      if (!sample) { cutNode.hidden = true; return; }
      /* The embroidery is added to BOTH figures, so it cancels out of the
         difference and is deliberately not applied here. */
      cutNode.hidden = false;
      cutNode.textContent = tpl.replace('{p}', printMoney((anchor - v.price) / 100, sample));
    };

    const syncPrice = (v) => {
      syncPriceFigures(v);
      syncSubmitTotal();
    };

    const syncVariant = () => {
      const v = findVariant();
      if (v) {
        if (idField) idField.value = String(v.id);
        syncPrice(v);
      }
      if (submit) {
        /* Unavailable is not the same as unchosen: a combination the shop does
           not stock must not look buyable, and must say which it is. */
        const ok = !!(v && v.available);
        submit.disabled = !ok;
        submit.dataset.state = v ? (v.available ? 'ready' : 'sold-out') : 'incomplete';
      }
      /* SAID ONCE, HERE, so the sticky bar never works the variant out a second
         time. This function is already the only place that decides whether the
         garment can be bought; announcing from it is what keeps one answer on
         the page instead of two that agree until they do not. */
      box.dispatchEvent(
        new CustomEvent('cooper:pdp:buystate', {
          bubbles: true,
          detail: {
            available: !!(v && v.available),
            state: submit ? submit.dataset.state : '',
            /* The label alone, for the same reason the sticky bar reads it:
               the price sits inside this button now. */
            label: submit ? submitLabelText() : '',
          },
        }),
      );
    };

    for (const g of groups) {
      g.addEventListener('change', () => {
        syncVariant();
        /* The panel prints a multiple of the CHOSEN variant's price, so a colour
           or size that costs differently has to move it too. */
        syncPack();
        syncReceipt();
        /* AND THE PHOTOGRAPH. The gallery listens on the box rather than being
           called directly -- see the note beside the listener in wireGallery.
           findVariant() is called again rather than threaded out of syncVariant
           because that one returns nothing and this is the cheap read of an
           array that is already in memory. */
        syncExtras();
        /* И ЦЕНЫ РЕЙКИ: они кратны цене ВЫБРАННОГО варианта, а размеры коврика
           стоят по-разному — см. syncRail(). */
        /* ПОРЯДОК ВАЖЕН: syncPackPrices переписывает цены на ярлыках наборов,
           а syncPack и ценовой блок ЧИТАЮТ их строкой с этих же ярлыков. Если
           переставить, покупатель увидит прежнюю цену ещё один щелчок. */
        syncPackPrices();
        syncRail();
        syncLongBack();
        syncMedia();
      });
    }

    /* THE QUANTITY RAIL, for a product whose bundle is itself twice.
       The tiles are radios and the form carries a hidden `quantity`; without
       this the rail would move the highlight and post one every time. The
       field is written rather than the form read, because cooper-cart.js posts
       `new FormData(form)` verbatim and whatever sits in that field IS the
       line. Nothing here computes a discount: the discount is an automatic one
       in the admin and applies at the cart. */
    const qtyField = box.querySelector('[data-pdp-qty-field]');
    const qtyRail = box.querySelector('[data-pdp-qty-rail]');

    /* ОДИН НАБОР РЯДОВ НА ШАГ, НЕ ПО КОПИИ В КАЖДОЙ ПЛИТКЕ.

       Так было до 18.09.2026, и оттуда тянулся `liveHost()`: ряды лежали
       внутри плиток, `[data-pdp-extra="2"]` встречался столько раз, сколько
       плиток, и перед каждым чтением приходилось выяснять, чья копия сейчас
       живая. Ряд теперь один и стоит под дорожкой — выяснять нечего, а лишний
       просто спрятан. Разбор поломки — в шапке cooper-qty-rail.liquid. */
    const slotBox = box.querySelector('[data-pdp-slots]');
    const slots = slotBox ? [...slotBox.querySelectorAll('[data-pdp-extra]')] : [];

    /* HOW MANY THE RAIL IS ASKING FOR RIGHT NOW. One is the answer when there
       is no rail at all, which is every garment. */
    const qtyPicked = () => {
      if (!qtyRail) return 1;
      const on = qtyRail.querySelector('input:checked');
      return on ? Number(on.dataset.pdpQty) || 1 : 1;
    };

    /* THE EXTRA COLOURS, RESOLVED TO VARIANTS AT THE CHOSEN SIZE. Colour is
       asked again per mat; size is not, so every line carries the size picked
       in the step above. A colour whose variant does not exist at that size is
       dropped rather than guessed — silence beats the wrong cloth.

       РЯД НОМЕР ОДИН ПРОПУСКАЕТСЯ: это сам вариант, и он уже уходит в корзину
       отдельной строкой. Посчитать его здесь значило бы положить лишний коврик. */
    const extraVariantIds = () => {
      const base = findVariant();
      if (!base || !slots.length) return [];
      const sizeIdx = base.options.length - 1;
      const out = [];
      for (const row of slots) {
        if (row.hidden) continue;
        if (Number(row.dataset.pdpExtra) < 2) continue;
        const on = row.querySelector('input:checked');
        if (!on) continue;
        const hit = variants.find(
          (v) => v.options.includes(on.value) && v.options[sizeIdx] === base.options[sizeIdx] && v.available
        );
        if (hit) out.push(hit.id);
      }
      return out;
    };

    /* ЦЕНЫ РЕЙКИ СЧИТАЮТСЯ ОТ ВЫБРАННОГО ВАРИАНТА, А НЕ ОТ ТОВАРА.

       Поломка, пойманная владельцем 18.09.2026 на живой странице: при выбранном
       XL за 39,90 € рейка печатала «Just one 25,00 €», «Two mats 42,50 €». В
       разметке стоит `p.price` — это МИНИМАЛЬНАЯ цена товара, то есть S, и
       Liquid отдаёт её один раз, ничего не зная о будущем выборе размера.

       СВОЙ ФОРМАТТЕР, А НЕ formatMultiple: тот объявлен ниже по файлу в том же
       замыкании, а syncRail() зовётся на инициализации — обращение к нему
       упало бы во временную мёртвую зону. Приём тот же и описан в шапке файла:
       Intl здесь запрещён, разделители берутся из строки, которую напечатал сам
       магазин, иначе одна панель скажет 41,80, а соседняя 41.80. */
    const railMoney = (variant, minor) => {
      const sample = (variant && variant.priceFormatted) || '';
      const digits = sample.match(/[\d.,\s ]+/);
      if (!digits) return '';
      const whole = (minor / 100).toFixed(2);
      const decimalComma = digits[0].includes(',') && !digits[0].trim().endsWith('.');
      return sample.replace(digits[0], decimalComma ? whole.replace('.', ',') : whole);
    };

    const saveTpl = qtyRail ? qtyRail.dataset.pdpSaveTpl || '' : '';

    const syncRail = () => {
      if (!qtyRail) return;
      const v = findVariant();
      if (!v || typeof v.price !== 'number') return;
      for (const input of qtyRail.querySelectorAll('input[data-pdp-qty]')) {
        const n = Number(input.dataset.pdpQty) || 1;
        const pct = Number(input.dataset.pdpPct) || 0;
        const full = v.price * n;
        /* К БЛИЖАЙШЕМУ ЦЕНТУ. Скидку даёт автоматическая скидка Shopify, и
           округляет она так же; пол-цента врозь — это ярлык, расходящийся с
           корзиной на копейку, то есть ровно то, что покупатель замечает. */
        const cut = Math.round((full * pct) / 100);
        const cell = input.closest('.cooper-pdp__pack');
        if (!cell) continue;
        const priceNode = cell.querySelector('[data-pdp-pack-price]');
        if (priceNode) priceNode.textContent = railMoney(v, full - cut);
        const saveNode = cell.querySelector('[data-pdp-pack-save]');
        if (saveNode && saveTpl) saveNode.textContent = saveTpl.replace('{p}', railMoney(v, cut));
      }
    };

    /* ФОТО ПОД ВЫБРАННЫЙ ЦВЕТ — И НА ЗАГРУЗКЕ ТОЖЕ.

       Поломка, пойманная владельцем 18.09.2026: страница открывалась с
       отмеченным Sky Blue, а в галерее висел bowl-mat-brown-02.png. Снимок
       менялся только по `change`, то есть покупатель, ничего не трогавший,
       видел коричневый коврик под подписью «Sky Blue». То же случилось бы с
       любой ссылкой `?variant=`, которыми Shopping и Merchant торгуют напрямую.

       Галерея слушает событие на box, а не зовётся напрямую, — см. заметку у
       слушателя в wireGallery. Порядок безопасен: wireGallery(box) отрабатывает
       раньше wire(box), так что к моменту первой отправки слушатель уже стоит. */
    const syncMedia = () => {
      const picked = findVariant();
      if (picked && picked.mediaId) {
        box.dispatchEvent(new CustomEvent('cooper:pdp:media', {detail: {mediaId: picked.mediaId}}));
      }
    };

    const colourPos = qtyRail ? Number(qtyRail.dataset.pdpColourPos) || 0 : 0;

    /* THE COLOUR THE FIRST STEP IS SHOWING RIGHT NOW. Every extra mat starts
       there: it is an answer the buyer has already given, and repeating the
       question is work he did not ask for. */
    const baseColour = () => {
      const v = findVariant();
      return v ? v.options[colourPos] : '';
    };

    /* ЦВЕТ ПО НАЗВАНИЮ. Палитра живёт только в разметке кружков, поэтому ряд
       без кружков (первый, он же эхо варианта) берёт заливку у соседей. */
    const hexOf = (name) => {
      if (!slotBox || !name) return '';
      for (const alt of slotBox.querySelectorAll('input[data-extra-colour]')) {
        if (alt.value === name) return alt.dataset.extraHex || '';
      }
      return '';
    };

    /* A ROW FOLLOWS THE FIRST STEP UNTIL IT IS TOUCHED. `data-owned` is set the
       moment the buyer picks a dot himself, and from then on the row keeps his
       choice however he changes step one — anything else would silently undo a
       decision he made. */
    const paintRow = (row) => {
      const face = row.querySelector('[data-extra-face]');
      const name = row.querySelector('[data-extra-name]');
      const inputs = [...row.querySelectorAll('input[data-extra-colour]')];
      let want = baseColour();
      if (row.dataset.owned === '1') {
        const on = row.querySelector('input:checked');
        if (on) want = on.value;
      }
      for (const input of inputs) {
        if (input.value === want && !input.checked) input.checked = true;
      }
      if (face) face.setAttribute('style', 'background-color: ' + (hexOf(want) || '#ccc') + ';');
      if (name) name.textContent = want || '';
    };

    const syncExtras = () => {
      const want = qtyPicked();
      if (slotBox) slotBox.hidden = !slots.length || want < 2;
      for (const row of slots) {
        row.hidden = Number(row.dataset.pdpExtra) > want;
        if (!row.hidden) paintRow(row);
      }
    };

    /* Picking a dot marks the row as the buyer's and repaints its summary.
       Ничего не закрывается: закрывать больше нечего — список стоит в потоке. */
    if (slotBox) {
      slotBox.addEventListener('change', (e) => {
        const on = e.target;
        if (!on || !on.dataset || !on.dataset.extraColour) return;
        const row = on.closest('[data-pdp-extra]');
        if (!row) return;
        row.dataset.owned = '1';
        paintRow(row);
        syncReceipt();
      });
    }

    if (qtyField && qtyRail) {
      qtyRail.addEventListener('change', (e) => {
        const n = e.target && e.target.dataset && e.target.dataset.pdpQty;
        if (!n) return;
        /* THE FIELD STAYS AT ONE WHENEVER THE COLOURS ARE CHOSEN SEPARATELY:
           the lines are posted by hand below, and a quantity of two on top of
           two posted lines would put four mats in the cart. */
        qtyField.value = slots.length ? '1' : String(n);
        syncExtras();
        syncReceipt();
      });
      syncExtras();
      syncRail();
    }

    /* Every piece's field, not just the first: the receipt reads all of them.

       AND THE PRICE, because the embroidery charge appears the moment a word is
       typed. Binding this to the receipt alone was the other half of the price
       being wrong: even with the total computed correctly, nothing recomputed it
       between choosing a size and pressing the button. */
    for (const field of box.querySelectorAll('[data-pdp-line]')) {
      /* THE BLANK-GARMENT GUARD MOVES WITH THE FIELD. The two property controls
         have to be out of the form for the whole time the field is empty, not
         only at the instant the button is pressed, so the DOM is kept honest on
         every keystroke. `change` as well as `input`, because an autofill or a
         paste chosen from the context menu can land a value without an input
         event. */
      field.addEventListener('change', syncCartProps);
      field.addEventListener('input', () => {
        syncCartProps();
        /* AND WHAT IS REQUIRED, because emptying a field behind a closed door
           is the one route that reached the browser's unfocusable-control
           refusal without any other event firing in between. */
        syncRequired();
        syncReceipt();
        syncVariant();
      });
    }

    /* THE THREAD AND THE COMPANION MOVE THE OVERVIEW TOO.

       Measured before this: syncReceipt was wired to the option groups and to
       typing, and to nothing else. Clicking a thread repainted the swatch and the
       collapsed summary and left the overview showing whatever had been chosen
       when a word was last typed -- so typing first and colouring after, which is
       the natural order, left the last panel before the money printing the
       default colour for every piece. The read was always correct; nothing asked
       it to read again.

       DELEGATED ON THE CONTAINER, not bound per radio, so a picker that is
       revealed or rebuilt later needs nobody to remember to re-bind it. */
    box.addEventListener('change', (e) => {
      const t = e.target;
      if (!t || !t.matches) return;
      if (t.matches('[data-pdp-thread], [data-mate-select]')) syncReceipt();
    });

    /* ----------------------------------------------------------------------
       THE PACK — the upsell inside the buy box.

       A pack is a COUNT OF THIS GARMENT, not a separate bundle product, and each
       piece gets its own cart line so it can carry its own words. Nothing here
       shows or implies a saving: the badges are rendered by Liquid only when the
       owner has confirmed the automatic discounts exist, and this file never
       writes one.
       -------------------------------------------------------------------- */
    const packInputs = [...box.querySelectorAll('[data-pdp-packs] input')];
    const lines = [...box.querySelectorAll('[data-pdp-line]')];
    const pieceBoxes = [...box.querySelectorAll('[data-pdp-piece]')];
    const panelName = box.querySelector('[data-pdp-panel-name]');
    const panelTotal = box.querySelector('[data-pdp-panel-total]');
    const panelNote = box.querySelector('[data-pdp-panel-note]');

    const tileGroups = [...box.querySelectorAll('[data-pack-tiles]')];

    /* THE PRICE ROW FOLLOWS THE PACK, and the figure is read off the option
       the buyer just pressed rather than recomputed here. The label already
       prints the computed set price in .cooper-pdp__pack-price — it is the
       same arithmetic the rail invites the buyer to check — so taking the
       string is both cheaper and guaranteed to agree with what he can see.

       WHY IT WAS WORTH FINDING. Measured 4 September 2026: choosing "With a
       dog bandana €42,90 SAVE €6,90" left the price row reading "€29,90
       €34,90 SAVE €5,00". The buyer on the most expensive route was shown
       arithmetic that does not close — 29,90 + 19,90 is 49,80, not the 42,90
       promised — and two different savings on one screen. */
    /* ЦЕНЫ НАБОРОВ СЧИТАЮТСЯ ОТ ВЫБРАННОГО ВАРИАНТА.

       Поломка, названная владельцем 19.09.2026 точнее некуда: «та же ошибка,
       что на ковриках — если я меняю размер, меняется цена, но не меняется цена
       на бандле». На коврике это была рейка количества, печатавшая от
       `p.price`; здесь — блоки наборов, печатающие от `product.price_min`.
       Оба раза это МИНИМАЛЬНАЯ цена товара, то есть S.

       Пока все размеры стоили одинаково, разницы не было. У худи лестница
       29,90 → 49,90 по восьми размерам, и на 5XL набор показывался посчитанным
       от 29,90.

       Слагаемые приходят из разметки (`data-pack-mate-cost`, `data-pack-save`,
       `data-pack-firm`) — там их посчитал Liquid, который знает про коллекции
       компаньонов и про их однородность по цене. Здесь только арифметика:
           цена варианта + стоимость компаньонов − скидка набора

       Ярлык экономии НЕ трогаем: `save_amount` — фиксированная сумма в евро, от
       размера она не зависит.

       СВОЙ ФОРМАТТЕР, как и у рейки: Intl в этом файле запрещён, разделители
       берутся из строки, которую напечатал сам магазин. */
    const packMoney = (variant, minor) => {
      const sample = (variant && variant.priceFormatted) || '';
      const digits = sample.match(/[\d.,\s ]+/);
      if (!digits) return '';
      const whole = (minor / 100).toFixed(2);
      const decimalComma = digits[0].includes(',') && !digits[0].trim().endsWith('.');
      return sample.replace(digits[0], decimalComma ? whole.replace('.', ',') : whole);
    };

    /* УДЛИНЁННАЯ СПИНА: показать и ВКЛЮЧИТЬ только на тех размерах, где она
       есть в лекале (M +7, L +10, XL +7, 2XL +7).

       Появилось после вопроса владельца 19.09.2026: в размерном гиде стояло
       «напишите нам до заказа», а писать было некуда — поля не существовало.

       ОТКЛЮЧАЕМ, А НЕ ПРЯЧЕМ. Спрятанный чекбокс браузер отправляет,
       отключённый — нет. Без этого покупатель, отметивший удлинение на M и
       передумавший в пользу 3XL, увёз бы в заказ галочку на размер, которого
       мы в этом крое не шьём.

       Размер ищется среди значений варианта, а не по индексу оси: у разных
       товаров оси в разном порядке, а на /de названия осей ещё и переведены. */
    const longBack = box.querySelector('[data-pdp-longback]');
    const longBackInput = longBack ? longBack.querySelector('[data-pdp-longback-input]') : null;
    const longBackCm = longBack ? longBack.querySelector('[data-pdp-longback-cm]') : null;
    const longBackMap = {};
    if (longBack) {
      for (const pair of (longBack.dataset.pdpLongbackMap || '').split(',')) {
        const [k, v] = pair.split(':').map((x) => (x || '').trim());
        if (k && v) longBackMap[k] = v;
      }
    }

    /* ПУСТОЕ ПОЛЕ НЕ УЕЗЖАЕТ В ЗАКАЗ. Приём тот же, что у «ваших слов» в
       syncCartProps(): у пустого поля снимается атрибут `name`, и браузер его
       не отправляет. Пустая строка в заказе — мусор, который закройщик обязан
       прочитать и отбросить. */
    const longBackProp = longBackInput ? longBackInput.dataset.pdpLongbackProp || '' : '';
    const syncLongBackName = () => {
      if (!longBackInput) return;
      const filled = longBackInput.value.trim().length > 0 && !longBackInput.disabled;
      if (filled) longBackInput.setAttribute('name', longBackProp);
      else longBackInput.removeAttribute('name');
    };

    const syncLongBack = () => {
      if (!longBack || !longBackInput) return;
      const v = findVariant();
      const size = v ? v.options.find((o) => Object.prototype.hasOwnProperty.call(longBackMap, o)) : null;
      const cm = size ? longBackMap[size] : null;
      longBack.hidden = !cm;
      longBackInput.disabled = !cm;
      /* НА НЕПОДХОДЯЩЕМ РАЗМЕРЕ ПОЛЕ ЧИСТИТСЯ. Оставить написанное «такса» на
         3XL значило бы хранить заказ на крой, которого в этом размере нет в
         лекале, и ждать, пока покупатель вернётся на M. */
      if (!cm) longBackInput.value = '';
      if (longBackCm) longBackCm.textContent = cm ? '+' + cm + ' cm' : '';
      syncLongBackName();
    };

    if (longBackInput) longBackInput.addEventListener('input', syncLongBackName);

    const packTrack = box.querySelector('[data-pdp-packs]');
    const packFromTpl = packTrack ? packTrack.dataset.pdpPackFromTpl || '' : '';

    const syncPackPrices = () => {
      if (!packInputs.length) return;
      const v = findVariant();
      if (!v || typeof v.price !== 'number') return;
      for (const input of packInputs) {
        const cell = input.closest('label');
        if (!cell) continue;
        const node = cell.querySelector('.cooper-pdp__pack-price');
        if (!node) continue;
        const mates = Number(input.dataset.packMateCost) || 0;
        const save = Number(input.dataset.packSave) || 0;
        const total = v.price + mates - save;
        const money = packMoney(v, total);
        if (!money) continue;
        /* «от» — это про разнобой цен ВНУТРИ категории компаньонов, а не про
           размер. Флаг пришёл из Liquid, где он и вычислялся. */
        node.textContent =
          input.dataset.packFirm === '0' && packFromTpl
            ? packFromTpl.replace('{p}', money)
            : money;
      }
    };

    const currentPack = () => {
      const picked = packInputs.find((i) => i.checked);
      if (!picked) return {pieces: 1, label: '', note: '', index: 0, priceText: '', el: null};
      const priceEl = picked.closest('label') && picked.closest('label').querySelector('.cooper-pdp__pack-price');
      return {
        pieces: Math.max(1, Number.parseInt(picked.dataset.packPieces, 10) || 1),
        label: picked.dataset.packLabel || '',
        note: picked.dataset.packNote || '',
        index: Number.parseInt(picked.dataset.packIndex, 10) || 0,
        priceText: priceEl ? priceEl.textContent.trim() : '',
        el: picked,
      };
    };

    /**
     * THE COMPANION VARIANTS THE CHOSEN PACK WILL ACTUALLY ADD.
     *
     * Read off the VISIBLE tile group only. Every pack's tiles stay in the DOM
     * so a colour chosen for the bandana survives switching pack and switching
     * back — which means the submit has to ask which group is showing rather
     * than collecting every select on the page and charging for all of them.
     */
    /* THE SELECTS LIVE ON THE PIECE ROWS NOW, one per pack, so they are found by
       their pack attribute rather than inside a tile group. The hidden test does
       the rest of the work: it drops the packs that are not chosen AND the piece
       rows this pack does not include, which is exactly the set the cart wants.

       DOM ORDER IS THE PAIRING. querySelectorAll returns row 2 before row 3, and
       the caller pairs mate[0] with piece 2's words — the same alignment the
       summary uses. */
    /* THE MARKS CLEAR THEMSELVES, so a refusal can never outlive its cause.
       Choosing a garment fires a `change` on the hidden input --
       assets/cooper-companion.js dispatches one so the buy box can recompute --
       and switching pack calls this too. */
    const clearMateMarks = () => {
      for (const t of box.querySelectorAll('[data-mate-tile][data-needed]')) {
        t.removeAttribute('data-needed');
      }
      for (const n of box.querySelectorAll('[data-mate-need]')) {
        n.hidden = true;
      }
    };

    box.addEventListener('change', (e) => {
      const t = e.target;
      if (!t || !t.matches) return;
      if (t.matches('[data-mate-select]') || t.name === 'cooper-pack') clearMateMarks();
    });

    const chosenMates = (index) =>
      [...box.querySelectorAll('[data-mate-select][data-mate-pack="' + index + '"]')]
        .filter((s) => !s.closest('[hidden]'))
        .map((s) => Number.parseInt(s.value, 10))
        .filter((id) => Number.isFinite(id) && id > 0);

    /**
     * MONEY IS FORMATTED BY THE SHOP, not by this file. Shopify hands us an
     * integer in minor units and a formatted string for one unit; multiplying
     * the string is how a shop ends up printing "1 490,00 Kč2". So the multiple
     * is built from the integer and the currency format Liquid already gave us,
     * and when that cannot be done safely nothing is printed at all.
     */
    const formatMultiple = (variant, pieces) => {
      if (!variant || typeof variant.price !== 'number') return '';
      if (pieces === 1) return variant.priceFormatted || '';
      const total = variant.price * pieces;
      /* Reuse the shop's own formatting by swapping the digits inside the
         single-unit string. Locale-safe because the separators come from it. */
      const sample = variant.priceFormatted || '';
      const digits = sample.match(/[\d.,\s ]+/);
      if (!digits) return '';
      const whole = (total / 100).toFixed(2);
      const decimalComma = digits[0].includes(',') && !digits[0].trim().endsWith('.');
      const shown = decimalComma ? whole.replace('.', ',') : whole;
      return sample.replace(digits[0], shown);
    };

    const syncPack = () => {
      const pack = currentPack();

      /* Only the chosen pack's tiles are showing. Hidden groups keep their
         selections; the submit reads the visible group. */
      for (const group of tileGroups) {
        group.hidden =
          Number.parseInt(group.dataset.packTiles, 10) !== pack.index;
      }

      /* The same switch one level down, for the per-pack colour selects and
         garment names that now sit on the piece rows. A pack's options stay
         selected while another pack is being looked at. */
      for (const wrap of box.querySelectorAll('[data-mate-wrap]')) {
        wrap.hidden =
          Number.parseInt(wrap.dataset.mateWrap, 10) !== pack.index;
      }

      for (const el of pieceBoxes) {
        const n = Number.parseInt(el.dataset.pdpPiece, 10);
        const on = n <= pack.pieces;
        el.hidden = !on;
        const field = el.querySelector('[data-pdp-line]');
        /**
         * A HIDDEN FIELD MUST NOT KEEP ITS WORDS IN THE ORDER. It stays in the
         * DOM so that switching pack and back does not lose what was typed, but
         * the submit only reads the fields that are showing — see below.
         *
         * AND THE DEFAULT FOR A NEW PIECE IS PIECE ONE'S WORDS. Most people
         * buying two want the same line twice; a blank field reads as an
         * unfinished form, which is the commonest reason a personalised order is
         * abandoned halfway.
         */
        if (on && field && !field.value && line && line.value) {
          field.value = line.value;
        }
      }

      if (panelName) panelName.textContent = pack.label;
      if (panelNote) panelNote.textContent = pack.note;
      if (panelTotal) panelTotal.textContent = formatMultiple(findVariant(), pack.pieces);

      applyLimit();
    };

    for (const input of packInputs) {
      input.addEventListener('change', syncPack);
      /* И цену тоже: syncPack переставляет плитки и поля, а ценовой блок
         живёт в другой ветке и без этого остаётся на цене одного предмета. */
      input.addEventListener('change', syncVariant);
    }

    /**
     * THE SUBMIT IS ONLY INTERCEPTED WHEN THE PLAIN FORM CANNOT CARRY THE ORDER
     * — more than one piece, or the paid add-on ticked. Shopify's ordinary
     * product form posts exactly one line, so anything else has to go through
     * /cart/add.js. Leaving the single-piece path alone means the theme's own
     * cart drawer keeps working for the commonest order.
     */
    form.addEventListener('submit', async (event) => {
      /* THE LAST WORD ON WHAT A BLANK GARMENT POSTS. This listener is on the
         FORM and cooper-cart.js listens on the DOCUMENT, so on the bubble path
         this always runs first, and whatever it leaves in the DOM is what
         new FormData(form) collects a moment later. Measured: a probe listener
         on the form that called stopPropagation kept the document listener from
         running at all, and the FormData read at that instant carried no
         properties[] key.

         It is here as well as on the keystroke because it closes the holes a
         keystroke cannot. A page restored from history can arrive with a value
         in the field WITHOUT firing an input event. A written single-piece order
         falls through to the native POST whenever the add-on product is missing,
         and that order needs the name this call puts back. And any future code
         that writes line.value directly rather than through the keyboard fires
         no input event either -- applyLimit used to do exactly that, before it
         was stopped from truncating anything.

         syncRequired RIDES ALONG because this is also where the pack changes,
         and the pack decides which text fields exist on screen. Dropping from
         three pieces to one must take `required` off the two rows that just
         went away, or the form refuses to submit over a field nobody can see. */
      syncCartProps();

      syncRequired();

      const pack = currentPack();

      /* ------------------------------------------------------------------
         A CHOSEN BUNDLE MUST HAVE ITS PIECES BEFORE IT CAN BE BOUGHT.

         Measured before this existed, on two product pages: choosing "With a
         bandana  42,90  SAVE 6,90" and pressing Add to cart without opening the
         picker put ONE garment in the cart at 29,90 with no discount, no error
         and no explanation. chosenMates() drops an unfilled tile by design --
         that is how an untouched tile avoids posting a garbage line -- so an
         unfilled tile and a tile that was never offered look identical to it.
         The pack promised two pieces, one arrived, nothing objected.

         The claim is made here, so the check belongs here. Refuse the submit,
         mark the tile that is empty (and only that one), and put the focus on
         the button that opens its picker, so the fix is one press from the
         sentence asking for it.

         BEFORE THE EARLY RETURN BELOW, which hands a single plain garment to the
         native POST -- a two-piece pack must never reach it.
         ------------------------------------------------------------------ */
      const mateFields = [
        ...box.querySelectorAll('[data-mate-select][data-mate-pack="' + pack.index + '"]'),
      ].filter((s) => !s.closest('[hidden]'));
      const unfilled = mateFields.filter((s) => {
        const id = Number.parseInt(s.value, 10);
        return !(Number.isFinite(id) && id > 0);
      });

      if (unfilled.length) {
        event.preventDefault();
        event.stopPropagation();
        for (const s of mateFields) {
          const t = s.closest('[data-mate-tile]');
          if (t) t.dataset.needed = unfilled.indexOf(s) >= 0 ? 'true' : 'false';
        }
        const firstTile = unfilled[0].closest('[data-mate-tile]');
        const note =
          firstTile &&
          firstTile.parentElement &&
          firstTile.parentElement.parentElement &&
          firstTile.parentElement.parentElement.querySelector('[data-mate-need]');
        if (note) note.hidden = false;
        const opener = firstTile && firstTile.querySelector('[data-companion-open]');
        if (opener) {
          opener.scrollIntoView({block: 'center', behavior: 'smooth'});
          opener.focus();
        }
        return;
      }

      clearMateMarks();

      /**
       * HOW MANY PIECES ARE ACTUALLY WRITTEN ON.
       *
       * The embroidery is charged per piece that carries words, because that is
       * what the work is: a second garment with a second line is a second run of
       * the machine. A piece left blank costs nothing, which is why this counts
       * rather than reading a checkbox — there is no checkbox any more, only
       * words to type or leave alone.
       */
      const writtenCount = lines.filter((f) => {
        const n = Number.parseInt(f.dataset.piece, 10) || 1;
        return n <= pack.pieces && f.value.trim().length > 0;
      }).length;

      const addonId = addon
        ? Number.parseInt(addon.dataset.addonVariant, 10)
        : NaN;
      const chargeAddon = writtenCount > 0 && Number.isFinite(addonId) && addonId > 0;

      /* A ZERO-PRICED LINE BREAKS AddToCart, AND META REPORTED IT.

         Events Manager, 4 September 2026: "Value field is missing", AddToCart,
         13% of events, integration Meta Pixel. Its suggested fix — open the
         code and find fbq('track', 'AddToCart') — does not apply here: this
         theme contains no fbq call at all (measured: zero matches in every
         asset and section), the pixel arrives through Shopify's
         web-pixels-manager, and its instrumentation publishes one
         product_added_to_cart per CART LINE.

         So the missing value is not a missing field. It is a line whose value
         really is zero: the Embroidery add-on, variant 58550077292879, priced
         0,00 EUR while stitching is free. Meta requires a value strictly
         greater than zero, so every add that carried words produced one valid
         AddToCart for the garment and one invalid one for the add-on.

         AND THE LINE CARRIES NOTHING UNIQUE. The thread and the words are
         already properties of the garment line — measured on the live product
         page, the form posts properties[Your line] and properties[Thread] —
         so the add-on was restating the thread on a line that costs nothing.

         addonCents() already returns 0 for a free add-on; it was only ever
         consulted for the displayed price. Consulting it here means the line
         returns by itself the day embroidery is charged for again, with no
         second edit.

         chargeAddon itself is left alone on purpose: it also decides whether
         this handler takes over or the form goes to the cart drawer's own
         listener, and changing that would move a single written garment onto a
         different code path — a separate change with its own risk. */
      const addonPayable = chargeAddon && addonCents() > 0;

      /* The plain form carries exactly one line, so anything with a companion or
         a paid embroidery line has to go through the cart API. A single blank
         garment still posts the ordinary way and keeps the theme's cart drawer. */
      /* A SECOND MAT IS A SECOND LINE, so the rail joins the companion pack and
         the paid embroidery on the cart-API path. Without this clause the form
         posted one mat and the second colour was never asked for again. */
      const extraIds = extraVariantIds();
      /* A CHOSEN BANDANA IS A SECOND LINE, so it joins the pack and the paid
         embroidery on the cart-API path. Without this clause the native POST
         would carry the garment alone and the bandana would be silently
         dropped -- the same shape of bug the mats clause above was written for. */
      if (pack.pieces <= 1 && !chargeAddon && !extraIds.length) return;

      const variantId = Number.parseInt(idField && idField.value, 10);
      if (!variantId) return;

      /* TWO HANDLERS WERE ADDING THE SAME GARMENT, AND THIS ONE LINE IS THE FIX.
         assets/cooper-cart.js listens for `submit` ON THE DOCUMENT and posts the
         form's own FormData to /cart/add.js, which is what makes the drawer work
         for an ordinary one-garment add. This listener is on the FORM, so it runs
         first, posts its own {items:[garment, embroidery]} and then navigates.
         preventDefault stops the browser submitting the form -- it does NOT stop
         the event bubbling to the document -- so the cart's listener ran straight
         afterwards and added the garment a SECOND time.

         Measured on four clean trials, cart read from /cart.js each time: a navy
         cap with "COOPER" produced Navy Cap S x2 plus Embroidery x1, 76.70 euro
         where 41.80 was correct. Where this handler bailed out early instead, the
         cart handler ran alone and the 6.90 embroidery line was simply lost. Two
         POSTs to /cart/add.js are visible in the network log for one tap.

         stopPropagation keeps the event on the form. The cart's document listener
         is unreachable for THIS form and untouched for every other add on the
         site -- the drawer's own upsell buttons and Ella's forms still work
         through it. */
      event.preventDefault();
      event.stopPropagation();
      if (submit) submit.disabled = true;

      /* THE PROPERTY NAME COMES FROM WHAT LIQUID SERVED, NOT FROM THE LIVE
         ATTRIBUTE. Reading line.name here was correct only while nothing ever
         took that attribute off, and the blank-garment guard above takes it off
         precisely when the field is empty. Measured: with the attribute gone,
         line.name reads "" and this expression produced "" -- a property with an
         empty NAME, on the one route that still reaches this line with piece one
         blank, which is a two- or three-piece pack whose words are on the
         companion. A constant captured at wire time cannot drift.

         The thread's own name is derived a few lines below and is deliberately
         left alone: nothing in this file mutates a radio's name, so that
         derivation still reads what was served -- measured, it still returns
         "properties[Thread]" with the radios orphaned. If a radio's name ever
         starts being mutated, that derivation has to move up here too. */
      const propName = linePropServed
        ? linePropServed.replace(/^properties\[|\]$/g, '')
        : 'Your line';
      const items = [];

      /**
       * PIECE ONE IS THIS GARMENT. Pieces two and three are the COMPANIONS the
       * pack adds, each its own product with its own chosen colour — so their
       * lines carry a different variant id, not another of this one. Getting
       * that wrong would charge for two hats and ship no bandana.
       *
       * Each line takes the words typed in the field with the matching piece
       * number, so the bandana can answer what the hat says.
       */
      const mates = chosenMates(pack.index);

      /**
       * THE THREAD HAS TO BE COPIED IN BY HAND, and leaving it out was a real
       * lost order rather than a tidiness problem.
       *
       * The radios are named properties[Thread], which reaches the cart ONLY on
       * the native form POST — and this handler calls preventDefault() the
       * moment anything is written, which is the ordinary case. So every
       * embroidered order went to the cart with the words and no thread colour,
       * while the section's own comment promised it would be on the packing slip
       * beside them. Read here and attached to each line that carries words:
       * the thread is a property OF the words, so a blank piece does not need
       * it, and the embroidery line gets it too because that is the line the
       * work is billed on.
       */
      /* THE THREAD IS PER PIECE NOW, so it is looked up by piece number rather
         than read once. The property NAME still comes from the setting — only
         piece one's radios carry it in their own `name`, the rest use plain
         group names because the script is what puts them in the cart. */
      const threadProp = (
        box.querySelector('[data-pdp-thread]') &&
        box
          .querySelector('[data-pdp-thread]')
          .name.startsWith('properties[')
          ? box.querySelector('[data-pdp-thread]').name
          : 'properties[Thread]'
      ).replace(/^properties\[|\]$/g, '');

      const threadFor = (n) => {
        const picked = [...box.querySelectorAll('[data-pdp-thread]:checked')].find(
          (r) => (Number.parseInt(r.dataset.threadPiece, 10) || 1) === n,
        );
        return picked ? picked.value.trim() : '';
      };

      const wordsFor = (n) => {
        /* PLAIN MEANS PLAIN ON THIS PATH TOO, AND THAT TOOK A MEASUREMENT.

           syncCartProps guards the NATIVE post by taking the name off the field.
           This path never reads the name -- it reads the value -- so the guard
           did not reach it, and the two only ever met once the bandana add-on
           made a single plain garment take the items path for the first time.
           Measured on the live draft: Plain plus a bandana put `Your line:
           COOPER QA` on the hat from a value the buyer had typed and then
           switched away from. The value is deliberately kept when he switches
           back, so this is the read that has to ask the question. */
        if (!isPersonalised()) return {};
        const field = lines.find(
          (f) => (Number.parseInt(f.dataset.piece, 10) || 1) === n,
        );
        const words = field ? field.value.trim() : '';
        if (!words) return {};
        const props = {[propName]: words};
        const thread = threadFor(n);
        if (thread) props[threadProp] = thread;
        return props;
      };

      items.push({id: variantId, quantity: 1, properties: wordsFor(1)});

      /* THE EXTRA MATS, one line each and no properties: nothing is written on
         them, and an empty property bag is what the owner's rule calls for on a
         plain piece. They ride here rather than in `mates` because mates are a
         DIFFERENT product with their own colour and their own words, while
         these are this product at another colour. */
      for (const id of extraIds) items.push({id, quantity: 1});

      /* THE BANDANA CARRIES NOTHING. It is a different product from the one the
         words were written for, and inheriting `Your line` or `Thread` would put
         the buyer's name on a garment he never asked to have it stitched on --
         and onto the packing slip beside it. No properties key at all, rather
         than an empty one. */
      /* THE SET IS NOT CARRIED HERE ANY MORE. Each card posts its own line the
         moment it is ticked -- see the note on the toggle above -- so adding
         them again from this handler is what duplicated the primary. */

      mates.forEach((mateVariantId, i) => {
        items.push({
          id: mateVariantId,
          quantity: 1,
          properties: wordsFor(i + 2),
        });
      });

      /**
       * ONE EMBROIDERY LINE FOR THE WHOLE ORDER, quantity one.
       *
       * It used to bill per written piece, which charged a three-garment pack
       * three times for what is one setup. The rule is the owner's and it is
       * simple: the charge applies once if anything is written at all, and not
       * at all if nothing is.
       */
      if (addonPayable) {
        /* THE CHARGE LINE NAMES EVERY THREAD IN THE ORDER. With one thread it
           used to restate it; with a thread per piece it lists them, so the
           person at the machine can see the whole job on the line that pays for
           it. Blank pieces contribute nothing. */
        const threads = [];
        for (let n = 1; n <= pack.pieces; n += 1) {
          const words = wordsFor(n);
          const t = threadFor(n);
          if (Object.keys(words).length && t && !threads.includes(t)) {
            threads.push(t);
          }
        }
        items.push({
          id: addonId,
          quantity: 1,
          properties: threads.length ? {[threadProp]: threads.join(', ')} : {},
        });
      }

      const failNote = box.querySelector('[data-pdp-add-error]');
      const fail = (text) => {
        if (!failNote) return;
        failNote.textContent = text || '';
        failNote.hidden = !text;
      };
      fail('');

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({items}),
        });
        /* THE SERVER'S OWN SENTENCE, because it names the garment: Shopify
           answers a rejected add with `description` -- "Navy Cap - L is not
           available" -- and no wording typed into a setting can be that precise.
           Parsed before the status check because the body carries it on failure,
           and .catch keeps a non-JSON answer (a 502 HTML page) from throwing
           here instead of below. */
        const data = await res.json().catch(() => null);
        if (!res.ok) {
          const said = data && (data.description || data.message);
          const err = new Error(said || 'cart/add.js ' + res.status);
          /* THE ONLY THING THAT MAKES A MESSAGE QUOTABLE. Without this the catch
             has to guess from the text, and it guessed wrong: a dead connection
             throws a TypeError reading "Failed to fetch", which is Chrome's own
             wording -- a different string in every engine, untranslated, and
             nothing a buyer can act on. Measured before this: that string went
             on screen. Set only where a response body was actually read. */
          err.fromShopify = !!said;
          throw err;
        }

        /* THE DRAWER, NOT A PAGE LOAD. The owner: "если я выбираю бандл меня
           перекидывает на страницу карты а не на модал окно наше".

           assets/cooper-cart.js listens for this event on the document and
           refetches the cart as it opens, so this file needs to know nothing
           about how the drawer is built -- the same door the header icon uses.

           It also settles an inconsistency the walkthrough measured: a plain
           garment already opened the drawer, while anything written or bundled
           navigated away. Both open the drawer now.

           THE PAGE LOAD STAYS AS THE FALLBACK. On a template with no drawer
           mounted, dispatching into nothing would leave the buyer looking at the
           product page wondering whether his set went anywhere. */
        if (document.querySelector('[data-cooper-cart]')) {
          document.dispatchEvent(new Event('cooper:cart:open'));
          if (submit) submit.disabled = false;
        } else {
          window.location.assign('/cart');
        }
      } catch (e) {
        /* NEVER LEAVE THE BUTTON DEAD, AND NEVER LEAVE IT UNEXPLAINED. Handing
           the control back was already here; the silence was the bug. A buyer
           whose size sold out between load and tap pressed a button that came
           back to life and told him nothing, so the reasonable reading was that
           the shop is broken -- and the plain-form fallback cannot be used to
           recover, because it would drop the companions and the words.

           The server's own sentence when there is one, the typed fallback when
           the failure was the network and there is no sentence to quote. */
        console.warn('[pdp] multi-line add failed', e);
        fail(
          (e && e.fromShopify && String(e.message)) ||
            (failNote && failNote.dataset.fallback) ||
            'That did not go through. Please try again.',
        );
        if (submit) submit.disabled = false;
      }
    });

    syncVariant();
    syncPackPrices();
    syncLongBack();
    syncPack();
    applyLimit();
    syncReceipt();
    syncMedia();
  }

  bind(document);
  document.addEventListener('shopify:section:load', (event) => bind(event.target));
})();
